export interface Flashcard {
  id: number;
  question: string;
  answer: string;
  category: string;
  difficulty: "easy" | "medium" | "hard";
  explanation?: string;
}

export const flashcards: Flashcard[] = [
  // Property Ownership & Land Use Controls and Regulations (75 cards)
  {
    id: 1,
    question: "What is real property?",
    answer: "Land and anything permanently attached to it.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "easy",
    explanation: "Includes the surface, subsurface, and airspace, along with natural and man-made attachments like buildings or trees. Governed by real estate laws rather than personal property rules."
  },
  {
    id: 2,
    question: "What is personal property?",
    answer: "Movable items not attached to land.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "easy",
    explanation: "Also called 'chattel,' includes furniture, cars, and equipment. Transferred by a bill of sale instead of a deed."
  },
  {
    id: 3,
    question: "What is a fixture?",
    answer: "An item that was personal property but is now part of real property.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Determined by the MARIA test: Method of attachment, Adaptability, Relationship, Intention, and Agreement. Example: built-in appliances or light fixtures."
  },
  {
    id: 4,
    question: "What is a trade fixture?",
    answer: "Personal property installed by a tenant for business use.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Removable by the tenant before lease ends; if not removed, it becomes the landlord's property through accession. Example: salon sinks or restaurant ovens."
  },
  {
    id: 5,
    question: "What is metes and bounds?",
    answer: "A method of land description using distance and direction.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Describes property boundaries starting from a point of beginning (POB). Common in rural areas without subdivisions."
  },
  {
    id: 6,
    question: "What is the lot and block system?",
    answer: "Describes land based on recorded subdivision maps.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "easy",
    explanation: "Common in cities and subdivisions; refers to specific lot, block, and tract numbers from recorded plat maps."
  },
  {
    id: 7,
    question: "What is the rectangular survey system?",
    answer: "Land description based on meridians and baselines.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Uses townships and ranges measured in miles. Common in Western U.S., but not heavily used in California residential sales."
  },
  {
    id: 8,
    question: "What is an easement?",
    answer: "Right to use another person's land for a specific purpose.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Example: access roads or utility lines. Easements can be created by necessity, prescription, or agreement."
  },
  {
    id: 9,
    question: "What is an easement appurtenant?",
    answer: "Easement benefiting a neighboring property.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Requires a dominant and servient tenement. Example: shared driveway crossing another's land."
  },
  {
    id: 10,
    question: "What is an easement in gross?",
    answer: "A personal right to use land that is not tied to ownership of another property.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Common with utilities or pipelines; no dominant tenement exists."
  },
  {
    id: 11,
    question: "What is encroachment?",
    answer: "Physical intrusion onto another's property.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Examples include fences or driveways extending beyond boundaries. May cause legal disputes or require removal."
  },
  {
    id: 12,
    question: "What is a license in land use?",
    answer: "Revocable, nontransferable permission to use land.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "easy",
    explanation: "Not an interest in land; can be revoked at any time. Example: permission to fish on private property."
  },
  {
    id: 13,
    question: "What is zoning?",
    answer: "Local government regulation of land use.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Divides land into zones (residential, commercial, industrial). Administered by planning commissions; ensures orderly growth."
  },
  {
    id: 14,
    question: "What is a building code?",
    answer: "Standards for construction and safety.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "easy",
    explanation: "Regulates structural design, fire protection, and sanitation. Local authorities enforce compliance through permits and inspections."
  },
  {
    id: 15,
    question: "What is eminent domain?",
    answer: "Government's right to take private land for public use with compensation.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Requires just compensation and public purpose. Process called condemnation."
  },
  {
    id: 16,
    question: "What is police power?",
    answer: "State authority to regulate for public welfare.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Includes zoning, building codes, and health/safety regulations. No compensation required if within legal bounds."
  },
  {
    id: 17,
    question: "What is escheat?",
    answer: "Reversion of unclaimed property to the state.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "easy",
    explanation: "Occurs when a person dies intestate (without heirs or will). Ensures property ownership continuity."
  },
  {
    id: 18,
    question: "What is environmental law in California?",
    answer: "Regulations protecting natural resources and public health.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Includes CEQA (California Environmental Quality Act), Clean Water Act, and air quality standards."
  },
  {
    id: 19,
    question: "What are riparian water rights?",
    answer: "Right to use water from a natural watercourse adjoining land.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Owners have reasonable use but cannot harm downstream users. Applies to rivers and streams."
  },
  {
    id: 20,
    question: "What are appropriative water rights?",
    answer: "Right granted by permit to use water from a non-adjacent source.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Based on priority system — 'first in time, first in right.' Common in agricultural or municipal water use."
  },
  {
    id: 21,
    question: "What is ad valorem tax?",
    answer: "Property tax based on assessed value.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Levied annually by counties. Proposition 13 limits property tax rate to 1% of assessed value plus local assessments."
  },
  {
    id: 22,
    question: "What is a special assessment?",
    answer: "Tax for specific public improvements.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Funds local projects like sidewalks or sewers. Only affects benefiting properties."
  },
  {
    id: 23,
    question: "What is Mello-Roos tax?",
    answer: "Additional property tax for community facilities districts.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Used to fund new infrastructure in developing areas. Must be disclosed to buyers."
  },
  {
    id: 24,
    question: "What is the Subdivision Map Act?",
    answer: "State law regulating physical subdivision of land.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Ensures proper planning, utilities, and infrastructure before land division. Administered by local agencies."
  },
  {
    id: 25,
    question: "What is the Subdivided Lands Law?",
    answer: "Law governing sale or lease of subdivided property.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Administered by California DRE; protects buyers through public reports and disclosure requirements."
  },
  {
    id: 26,
    question: "What is the difference between police power and eminent domain?",
    answer: "Police power regulates use; eminent domain takes ownership.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Police power limits how property is used (no compensation). Eminent domain transfers ownership with compensation."
  },
  {
    id: 27,
    question: "What is condemnation?",
    answer: "Legal process of taking private property through eminent domain.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Owner must receive fair market value; government must prove public purpose."
  },
  {
    id: 28,
    question: "What are deed restrictions?",
    answer: "Private limitations on property use in a deed.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Set by developers or previous owners; enforceable by injunction. Example: limiting property to residential use only."
  },
  {
    id: 29,
    question: "What are CC&Rs?",
    answer: "Rules for homeowners in a subdivision or HOA.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Dictate use, design, and maintenance. Enforced by homeowners' association."
  },
  {
    id: 30,
    question: "What is an encumbrance?",
    answer: "Claim or liability affecting property title.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Includes liens, easements, or restrictions. Does not necessarily prevent sale but may affect value or use."
  },
  {
    id: 31,
    question: "What is a lien?",
    answer: "Legal claim on property to secure payment of a debt.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Examples: mortgage lien, mechanic's lien, tax lien. Must be satisfied before clear title transfer."
  },
  {
    id: 32,
    question: "What is a mechanic's lien?",
    answer: "Lien filed by contractors or suppliers for unpaid work.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Must be recorded within specific time limits; has priority over later liens."
  },
  {
    id: 33,
    question: "What is lis pendens?",
    answer: "Notice of pending legal action affecting title.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Warns potential buyers or lenders of ongoing disputes. Recorded in public records."
  },
  {
    id: 34,
    question: "What is an appurtenance?",
    answer: "Something attached to and part of the land.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "easy",
    explanation: "Includes easements, rights-of-way, or improvements that transfer with the property."
  },
  {
    id: 35,
    question: "What is the bundle of rights?",
    answer: "Set of ownership rights associated with real property.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Includes right to possess, control, enjoy, exclude, and dispose. Ownership may be limited by law or contract."
  },
  {
    id: 36,
    question: "What is a land use restriction?",
    answer: "Legal or private limitation on property use.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Examples: zoning laws, CC&Rs, or environmental protections. Ensures property use aligns with community standards."
  },
  {
    id: 37,
    question: "What are air rights?",
    answer: "Ownership or use rights to the airspace above a property.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "These rights can be leased or sold separately from the land itself. For example, developers might purchase air rights to build over existing structures or railways."
  },
  {
    id: 38,
    question: "What are subsurface rights?",
    answer: "Rights to minerals or resources beneath the land.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Owners may sell or lease these rights for oil, gas, or mineral extraction. In California, mineral rights can be separated from surface ownership."
  },
  {
    id: 39,
    question: "What are riparian rights?",
    answer: "Rights of property owners bordering natural watercourses.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Allow reasonable use of water from adjacent rivers or streams, provided it does not harm other users downstream."
  },
  {
    id: 40,
    question: "What are littoral rights?",
    answer: "Rights of property owners along navigable lakes or oceans.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Ownership typically extends to the mean high-water mark. The state retains ownership of the underlying land below that line."
  },
  {
    id: 41,
    question: "What is avulsion?",
    answer: "Sudden loss or gain of land due to natural causes.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "For example, a river changing course quickly. Ownership boundaries generally remain the same despite the shift."
  },
  {
    id: 42,
    question: "What is accretion?",
    answer: "Gradual addition of land by natural deposits like soil or sand.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Common along rivers or shorelines. The new land belongs to the property owner."
  },
  {
    id: 43,
    question: "What is erosion?",
    answer: "Gradual loss of land through natural forces like wind or water.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Reduces land size over time. The boundary changes naturally with the loss of land."
  },
  {
    id: 44,
    question: "What is reliction?",
    answer: "Gradual exposure of land previously under water.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Occurs when a lake or river recedes. The newly exposed land belongs to the riparian owner."
  },
  {
    id: 45,
    question: "What are public controls on land use?",
    answer: "Government regulations restricting property use.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "easy",
    explanation: "Includes zoning, building codes, subdivision laws, and environmental protections. Enforced by local and state agencies."
  },
  {
    id: 46,
    question: "What are private controls on land use?",
    answer: "Limitations created by private parties rather than government.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Examples: CC&Rs, HOA bylaws, and deed restrictions. Enforceable through civil actions or injunctions."
  },
  {
    id: 47,
    question: "What is a variance in zoning?",
    answer: "Permission to deviate from zoning requirements.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Granted by zoning boards for unique circumstances. Does not change the overall zoning classification."
  },
  {
    id: 48,
    question: "What is a conditional use permit (CUP)?",
    answer: "Allows land use not ordinarily permitted in a zone.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Issued for uses beneficial to the public, such as schools or hospitals in residential zones."
  },
  {
    id: 49,
    question: "What is a nonconforming use?",
    answer: "Existing property use that doesn't comply with current zoning.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Allowed to continue if established legally before zoning change; may lose status if use is discontinued."
  },
  {
    id: 50,
    question: "What is downzoning?",
    answer: "Change in zoning to a less intensive use.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Example: rezoning commercial property to residential. May reduce property value but serve public interest."
  },
  {
    id: 51,
    question: "What is spot zoning?",
    answer: "Zoning of a small area differently from surrounding properties.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Often controversial if it benefits one owner unfairly. Can be legally challenged as arbitrary."
  },
  {
    id: 52,
    question: "What are setback requirements?",
    answer: "Minimum distance buildings must be from property lines.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "easy",
    explanation: "Established in zoning ordinances to ensure light, air, and privacy between properties."
  },
  {
    id: 53,
    question: "What is a comprehensive plan (master plan)?",
    answer: "Long-term guide for community land use and development.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Covers zoning, transportation, utilities, and housing. Ensures coordinated and sustainable growth."
  },
  {
    id: 54,
    question: "What is CEQA?",
    answer: "Law requiring environmental review for significant projects.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Developers must prepare Environmental Impact Reports (EIRs) to disclose potential effects on the environment."
  },
  {
    id: 55,
    question: "What is Natural Hazard Disclosure (NHD)?",
    answer: "Report required for property sales disclosing natural hazard zones.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Includes flood, fire, earthquake fault, and seismic zones. Required by California law for residential sales."
  },
  {
    id: 56,
    question: "What is a seismic hazard zone?",
    answer: "Area prone to earthquake-related ground movement.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Properties in these zones require disclosure to buyers and may need geological inspections."
  },
  {
    id: 57,
    question: "What is a flood zone?",
    answer: "Area at risk of flooding as defined by FEMA maps.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Properties may require flood insurance and disclosure to buyers under federal and state law."
  },
  {
    id: 58,
    question: "What is a coastal zone?",
    answer: "Land within designated distance from California's coast regulated by the Coastal Act.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Development in this area requires permits from the California Coastal Commission to protect natural resources."
  },
  {
    id: 59,
    question: "What are mineral rights?",
    answer: "Ownership rights to underground minerals and resources.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "May be sold separately from surface rights. Surface owners may be limited in blocking access to mineral owners."
  },
  {
    id: 60,
    question: "What is a solar easement?",
    answer: "Right to receive sunlight across another's property.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Used to protect solar panels from shading by neighboring buildings or trees."
  },
  {
    id: 61,
    question: "What is a geological report?",
    answer: "Document assessing soil stability and seismic risks.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Often required before construction in areas with landslide or earthquake potential."
  },
  {
    id: 62,
    question: "What is a building permit?",
    answer: "Official authorization to begin construction or renovation.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "easy",
    explanation: "Ensures compliance with local building codes and zoning regulations."
  },
  {
    id: 63,
    question: "What is a certificate of occupancy?",
    answer: "Document certifying a structure is safe and compliant for use.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Issued after final inspection; required before occupancy or utility connection."
  },
  {
    id: 64,
    question: "What is a homeowner association (HOA)?",
    answer: "Organization managing a subdivision or condominium community.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Enforces CC&Rs, collects dues, and maintains common areas. Governed by board and bylaws."
  },
  {
    id: 65,
    question: "What is a title cloud?",
    answer: "Any issue or claim that may affect clear ownership of property.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Includes liens, easements, or unresolved ownership disputes. Usually cleared through title insurance or court action."
  },
  {
    id: 66,
    question: "What is a public report under the Subdivided Lands Law?",
    answer: "Disclosure document required before subdivided land can be sold.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Issued by the California DRE to ensure full disclosure of material facts to buyers."
  },
  {
    id: 67,
    question: "What is a land contract (contract for deed)?",
    answer: "Seller-financed agreement where title passes after full payment.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Buyer gains equitable title during the payment period; seller retains legal title as security."
  },
  {
    id: 68,
    question: "What are police power limitations?",
    answer: "Restrictions on ownership rights for public welfare.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Examples: zoning, rent control, and health regulations. Owners must comply even if it reduces value."
  },
  {
    id: 69,
    question: "What is public ownership?",
    answer: "Property held by government entities.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "easy",
    explanation: "Used for parks, schools, roads, and public utilities. Exempt from property taxes."
  },
  {
    id: 70,
    question: "What is private ownership?",
    answer: "Ownership by individuals or corporations.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "easy",
    explanation: "Subject to taxation, zoning, and other land-use regulations under state and local laws."
  },
  {
    id: 71,
    question: "What is dedication?",
    answer: "Voluntary transfer of land for public use.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "medium",
    explanation: "Developers often dedicate streets or parks to municipalities during subdivision approval."
  },
  {
    id: 72,
    question: "What is inverse condemnation?",
    answer: "Legal action by property owners against government for property loss.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Filed when government actions effectively take property value without compensation (e.g., nearby highway noise)."
  },
  {
    id: 73,
    question: "What is quiet enjoyment?",
    answer: "Right to use property without interference from others.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "easy",
    explanation: "Implied covenant in leases and deeds. Landlords must not disturb tenants' peaceful use of premises."
  },
  {
    id: 74,
    question: "What is adverse possession?",
    answer: "Acquiring ownership through continuous, hostile use of another's property.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Requires open, notorious, continuous possession for statutory period (typically 5 years in California). Must be hostile and without owner's permission."
  },
  {
    id: 75,
    question: "What is a prescriptive easement?",
    answer: "Easement acquired through continuous, adverse use over time.",
    category: "Property Ownership & Land Use Controls and Regulations",
    difficulty: "hard",
    explanation: "Similar to adverse possession but grants use rights rather than ownership. Must be open, notorious, continuous, and hostile for statutory period."
  },

  // Laws of Agency and Fiduciary Duties (85 cards)
  {
    id: 76,
    question: "What is agency?",
    answer: "A relationship where one person acts on behalf of another.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "easy",
    explanation: "In real estate, the agent (broker or salesperson) represents the principal (client) in dealings with third parties."
  },
  {
    id: 77,
    question: "What is a principal?",
    answer: "The person who authorizes an agent to act on their behalf.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "easy",
    explanation: "The principal can be a buyer, seller, landlord, or tenant who delegates authority to the agent via an agreement."
  },
  {
    id: 78,
    question: "What is an agent?",
    answer: "A person authorized to act for a principal in dealings with third parties.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "easy",
    explanation: "In California real estate, an agent must be a licensed broker or salesperson operating under a broker."
  },
  {
    id: 79,
    question: "What is fiduciary duty?",
    answer: "An agent's legal and ethical obligation to act in the best interest of the client.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Includes loyalty, obedience, confidentiality, disclosure, accounting, and reasonable care."
  },
  {
    id: 80,
    question: "What is loyalty?",
    answer: "Agent must place client's interests above their own.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Agents must avoid conflicts of interest and disclose any personal benefit related to the transaction."
  },
  {
    id: 81,
    question: "What is confidentiality?",
    answer: "Agent must protect client's private information.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Information about price, motivation, or strategy cannot be shared without consent, even after the relationship ends."
  },
  {
    id: 82,
    question: "What is disclosure?",
    answer: "Agent must reveal all material facts to the client.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Includes facts affecting value or desirability of property and information that could influence decisions."
  },
  {
    id: 83,
    question: "What is obedience?",
    answer: "Agent must follow all lawful instructions from the principal.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "easy",
    explanation: "An agent cannot obey illegal or unethical orders; duties are limited to lawful instructions."
  },
  {
    id: 84,
    question: "What is accounting?",
    answer: "Agent must properly handle and account for client funds.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Trust funds must be deposited in a broker trust account and cannot be commingled with personal funds."
  },
  {
    id: 85,
    question: "What is reasonable care and skill?",
    answer: "Agent must act with competence and diligence.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Agents must use their professional expertise to protect clients' interests and avoid negligence."
  },
  {
    id: 86,
    question: "What is the Agency Disclosure Law in California?",
    answer: "Law requiring agents to disclose agency relationships to all parties.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Mandates use of the 'Disclosure Regarding Real Estate Agency Relationship' form before entering into contracts."
  },
  {
    id: 87,
    question: "What is dual agency?",
    answer: "When one brokerage represents both buyer and seller in the same transaction.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Legal in California if both parties provide informed written consent. Agent must remain neutral and not favor either side."
  },
  {
    id: 88,
    question: "What is single agency?",
    answer: "Agent represents only one party—either buyer or seller.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "easy",
    explanation: "This relationship provides undivided loyalty to one principal and avoids conflicts of interest."
  },
  {
    id: 89,
    question: "What is a subagent?",
    answer: "An agent appointed by another agent to assist in representing the principal.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Subagents owe fiduciary duties to the same principal as the original agent. Must be disclosed and authorized."
  },
  {
    id: 90,
    question: "What is implied agency?",
    answer: "Created by conduct rather than written or verbal agreement.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Occurs when an agent acts on behalf of someone and that person accepts the actions. Risky without written authorization."
  },
  {
    id: 91,
    question: "What is express agency?",
    answer: "Created by explicit written or oral agreement.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "easy",
    explanation: "Listing agreements and buyer representation contracts are examples of express agency relationships."
  },
  {
    id: 92,
    question: "What is ostensible agency (apparent authority)?",
    answer: "When a principal allows others to believe an agency exists.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Occurs if the principal's actions mislead a third party. The principal may be bound by the agent's actions."
  },
  {
    id: 93,
    question: "What is ratification?",
    answer: "Approval of an unauthorized act by a principal.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "If a principal accepts benefits from an unauthorized act, agency is ratified retroactively."
  },
  {
    id: 94,
    question: "What is a listing agreement?",
    answer: "A contract between seller and broker to market a property.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Outlines the broker's authority, compensation, and duration. Must be in writing to be enforceable."
  },
  {
    id: 95,
    question: "What is an exclusive right to sell listing?",
    answer: "Gives broker sole right to earn commission regardless of who sells the property.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Most common listing type in California; ensures broker compensation even if owner finds the buyer."
  },
  {
    id: 96,
    question: "What is an exclusive agency listing?",
    answer: "Gives broker right to sell but allows owner to sell without paying a commission.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Broker only earns commission if they or another broker produce the buyer."
  },
  {
    id: 97,
    question: "What is an open listing?",
    answer: "Nonexclusive agreement allowing multiple brokers to sell the same property.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "easy",
    explanation: "Only the broker who procures the buyer receives commission. Owner can sell independently."
  },
  {
    id: 98,
    question: "What is a net listing?",
    answer: "Listing where broker's commission is any amount above the seller's net price.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Legal in California but discouraged due to conflict of interest and fiduciary concerns."
  },
  {
    id: 99,
    question: "What is a buyer representation agreement?",
    answer: "Contract between a buyer and broker for property purchase assistance.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Establishes agency relationship, broker duties, and compensation terms. May be exclusive or non-exclusive."
  },
  {
    id: 100,
    question: "What causes termination of agency?",
    answer: "Agency can end by mutual consent, expiration, performance, or law.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Also ends upon death, incapacity, bankruptcy, or destruction of property."
  },
  {
    id: 101,
    question: "What is power of attorney?",
    answer: "Written authorization for one person to act legally for another.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Can be general (broad powers) or special (limited purpose). Ends upon death or revocation."
  },
  {
    id: 102,
    question: "What is an attorney-in-fact?",
    answer: "Person authorized to act under a power of attorney.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Not necessarily a lawyer; must act strictly within granted powers."
  },
  {
    id: 103,
    question: "What is procuring cause?",
    answer: "The effort that directly results in a sale.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Determines which broker is entitled to a commission when multiple agents are involved."
  },
  {
    id: 104,
    question: "What is commission sharing?",
    answer: "Division of earned commission between cooperating brokers or agents.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Must be agreed upon in writing. Only licensed brokers can pay or receive real estate commissions."
  },
  {
    id: 105,
    question: "What is a trust account?",
    answer: "Broker's separate account for client funds.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Used for earnest money deposits and rents. Funds must not be commingled with the broker's personal or business money."
  },
  {
    id: 106,
    question: "What is commingling?",
    answer: "Mixing client trust funds with personal funds.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Prohibited under California law; can lead to license suspension or revocation."
  },
  {
    id: 107,
    question: "What is conversion?",
    answer: "Unauthorized use of client funds by a broker or agent.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "A serious violation that can result in criminal prosecution and permanent loss of license."
  },
  {
    id: 108,
    question: "What is an Agency Confirmation Form (AC Form)?",
    answer: "Form confirming agency relationships in a transaction.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Required to be signed by all parties in the purchase contract. Clarifies who each agent represents."
  },
  {
    id: 109,
    question: "What is broker supervision?",
    answer: "Broker's legal duty to oversee all agents under their license.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Includes reviewing contracts, maintaining trust records, and ensuring legal compliance."
  },
  {
    id: 110,
    question: "What is a material fact?",
    answer: "Any fact that affects a property's value or desirability.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Agents must disclose all material facts even if unfavorable to their client."
  },
  {
    id: 111,
    question: "What is misrepresentation?",
    answer: "False statement or omission of a material fact.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Can be innocent, negligent, or fraudulent. All can lead to liability, but fraudulent misrepresentation carries the harshest penalties."
  },
  {
    id: 112,
    question: "What is fraud?",
    answer: "Intentional deception for unlawful gain.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Occurs when an agent knowingly provides false information to induce another party to act. Results in severe civil and criminal penalties."
  },
  {
    id: 113,
    question: "What is negligence?",
    answer: "Failure to exercise reasonable care expected of a professional.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Occurs when an agent's careless actions or inactions cause harm or loss to a client. Not intentional but still a breach of duty."
  },
  {
    id: 114,
    question: "What is agency coupled with an interest?",
    answer: "Agency relationship where the agent has a financial interest in the subject property.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Cannot be revoked by the principal without the agent's consent. Common when agent finances part of a deal."
  },
  {
    id: 115,
    question: "What is the imputed knowledge rule?",
    answer: "Knowledge of the agent is legally imputed to the principal.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Even if the principal is unaware, they are deemed to know what their agent knows regarding the transaction."
  },
  {
    id: 116,
    question: "What is a third party?",
    answer: "Any person the agent deals with on behalf of the principal.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "easy",
    explanation: "Includes buyers, sellers, tenants, or others involved in a transaction but not the agent's client."
  },
  {
    id: 117,
    question: "What is a customer?",
    answer: "The party not represented by the agent in a transaction.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "easy",
    explanation: "Agents owe customers honesty and fair dealing but not fiduciary duties."
  },
  {
    id: 118,
    question: "What is a client?",
    answer: "The principal who has entered into an agency relationship with a broker or agent.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "easy",
    explanation: "Receives full fiduciary representation and loyalty from the agent."
  },
  {
    id: 119,
    question: "When must agency disclosure occur?",
    answer: "Disclosure must occur before or at the same time as signing a listing or purchase agreement.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Ensures all parties understand who represents whom to prevent undisclosed dual agency."
  },
  {
    id: 120,
    question: "What is an agent's duty to third parties?",
    answer: "Agent must treat all parties honestly and disclose material facts.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Even when representing one party, agents cannot mislead or conceal information from others."
  },
  {
    id: 121,
    question: "When is an agent entitled to commission?",
    answer: "Agent earns commission when they are the procuring cause of a ready, willing, and able buyer.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Payment depends on contract terms and performance; usually due when sale closes."
  },
  {
    id: 122,
    question: "What is a ready, willing, and able buyer?",
    answer: "A buyer who is qualified and prepared to purchase on the seller's terms.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "If seller rejects such an offer without justification, broker may still be owed commission."
  },
  {
    id: 123,
    question: "What is agency by necessity?",
    answer: "Agency created in emergency situations to protect property or prevent loss.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Agent acts without prior consent but in the best interest of the principal. Rare in real estate."
  },
  {
    id: 124,
    question: "What is agency by estoppel?",
    answer: "Prevents a person from denying an agency relationship if their actions led others to believe one existed.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Protects innocent third parties who relied on apparent authority."
  },
  {
    id: 125,
    question: "What is breach of fiduciary duty?",
    answer: "Failure to uphold duties owed to a client.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "May result in loss of commission, civil damages, or license revocation by the DRE."
  },
  {
    id: 126,
    question: "What is an independent contractor relationship?",
    answer: "Most agents work as independent contractors under a broker.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "They control their own work methods but must follow laws and broker supervision. Brokers do not withhold taxes."
  },
  {
    id: 127,
    question: "What is an employee relationship in real estate?",
    answer: "Agent works under broker's direct control and supervision.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Employer withholds taxes, provides benefits, and dictates working conditions. Less common in real estate."
  },
  {
    id: 128,
    question: "What is a broker associate?",
    answer: "A licensed broker who chooses to work under another broker.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "easy",
    explanation: "Has broker-level education but acts in salesperson capacity under the employing broker."
  },
  {
    id: 129,
    question: "What is DRE enforcement?",
    answer: "The California Department of Real Estate enforces licensing laws and investigates violations.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Can suspend or revoke licenses and impose fines for violations of agency or trust fund rules."
  },
  {
    id: 130,
    question: "What is the trust fund handling timeline?",
    answer: "Trust funds must be deposited, delivered, or mailed within three business days.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Ensures prompt handling of client money and prevents misuse or commingling."
  },
  {
    id: 131,
    question: "What is a fictitious business name (DBA)?",
    answer: "Business name different from the broker's legal name.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Must be registered with the DRE and county clerk. All advertising must include the broker's name and license number."
  },
  {
    id: 132,
    question: "What causes agency termination by law?",
    answer: "Agency automatically ends under certain legal events.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Includes death, incapacity, bankruptcy, or destruction of subject property."
  },
  {
    id: 133,
    question: "What is disclosure of conflicts of interest?",
    answer: "Agents must reveal any personal or financial interest in a transaction.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "For example, buying property for themselves or relatives without disclosure breaches fiduciary duty."
  },
  {
    id: 134,
    question: "What is disclosure of multiple offers?",
    answer: "Agent must present all offers promptly and disclose existence of competing offers.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Cannot reveal offer terms without the client's consent; ensures fair dealing among buyers."
  },
  {
    id: 135,
    question: "What is disclosure of material defects?",
    answer: "Agent must inform all parties of any known physical or legal defects.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Failure to disclose can result in civil liability, even if defect benefits their client."
  },
  {
    id: 136,
    question: "What is a conflict of interest?",
    answer: "When agent's personal interests interfere with client's best interests.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Must be disclosed immediately. Example: agent represents both buyer and seller in separate deals."
  },
  {
    id: 137,
    question: "What is an Agency Disclosure Form (AD Form)?",
    answer: "Mandatory California form explaining agency relationships.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "easy",
    explanation: "Describes duties owed to clients and customers. Required for all residential transactions."
  },
  {
    id: 138,
    question: "What are civil penalties for misrepresentation?",
    answer: "Legal damages owed for providing false information or concealing facts.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "In California, agents may be liable for actual, punitive, and treble damages under fraud statutes."
  },
  {
    id: 139,
    question: "What is a Broker Price Opinion (BPO)?",
    answer: "Estimate of property value prepared by a broker.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Used for lenders or clients when a full appraisal is not required. Must disclose that it is not an appraisal."
  },
  {
    id: 140,
    question: "What is agency compensation disclosure?",
    answer: "Agents must disclose how and by whom they are paid.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Transparency prevents disputes and ensures all parties understand financial incentives."
  },
  {
    id: 141,
    question: "What is secret profit?",
    answer: "Undisclosed financial gain obtained by an agent.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Violation of fiduciary duty; agent must surrender any secret profit to the principal."
  },
  {
    id: 142,
    question: "What is a kickback?",
    answer: "Illegal payment for referring business to another service provider.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Prohibited under RESPA and DRE regulations. Agents may not receive unearned fees."
  },
  {
    id: 143,
    question: "What are agency creation requirements?",
    answer: "Consent and control must exist between principal and agent.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Both parties must agree, and the principal must have capacity to contract. Compensation is not required for agency to exist."
  },
  {
    id: 144,
    question: "What is dual agency disclosure timing?",
    answer: "Must be disclosed as soon as it arises and confirmed in writing.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Failure to disclose dual agency can void commissions and lead to disciplinary action."
  },
  {
    id: 145,
    question: "What is the broker's record retention rule?",
    answer: "Brokers must keep transaction records for at least three years.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Includes contracts, trust account records, and correspondence. Ensures compliance and audit trail."
  },
  {
    id: 146,
    question: "What is Errors and Omissions Insurance (E&O)?",
    answer: "Protects brokers and agents against negligence claims.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Covers unintentional mistakes but not fraud, conversion, or criminal acts."
  },
  {
    id: 147,
    question: "What is the Real Estate Recovery Fund?",
    answer: "State fund compensating victims of licensee fraud or conversion.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Victims can recover up to $50,000 per transaction after court judgment; DRE may suspend the agent's license."
  },
  {
    id: 148,
    question: "What is ongoing duty to disclose?",
    answer: "Agent must update clients with new material information as it arises.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Duty continues throughout the transaction and ends only when the agency is terminated."
  },
  {
    id: 149,
    question: "What is honesty and fair dealing?",
    answer: "Ethical duty owed to all parties, not just the client.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "easy",
    explanation: "Applies even in non-agency relationships; ensures truthful communication and transparency."
  },
  {
    id: 150,
    question: "What is undisclosed dual agency?",
    answer: "Dual representation without informed consent of both parties.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Illegal in California; can result in loss of license and forfeiture of commission."
  },
  {
    id: 151,
    question: "What is supervision of salespersons?",
    answer: "Broker must provide active oversight of agents' activities.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Includes reviewing marketing, contracts, and disclosures to ensure legal compliance."
  },
  {
    id: 152,
    question: "What is the difference between agency and non-agency relationships?",
    answer: "Agency involves fiduciary duties; non-agency involves only fair dealing.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "For example, a transaction coordinator assists with paperwork but owes no fiduciary duty."
  },
  {
    id: 153,
    question: "What is ethical duty of disclosure?",
    answer: "Agent's moral responsibility to act transparently beyond legal minimums.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Aligns with NAR Code of Ethics and California Business & Professions Code 10176 and 10177."
  },
  {
    id: 154,
    question: "What are unlicensed assistants?",
    answer: "Individuals who may perform limited administrative tasks under broker supervision.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Cannot solicit clients, show property, or negotiate; may handle paperwork and advertising setup."
  },
  {
    id: 155,
    question: "What are team advertising rules in California?",
    answer: "Teams must include broker's name and license number in all marketing.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Team names may not imply separate brokerage or use misleading terms like 'realty' or 'company.'"
  },
  {
    id: 156,
    question: "What is broker liability for agent's actions?",
    answer: "Broker is responsible for acts of employed salespersons within the scope of their authority.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Applies even if broker was unaware of misconduct. Brokers must supervise to limit liability."
  },
  {
    id: 157,
    question: "What is revocation of agency by principal?",
    answer: "Principal can terminate agency at any time unless agency is coupled with an interest.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Revocation must be communicated to the agent; wrongful termination may lead to damages."
  },
  {
    id: 158,
    question: "What is disclosure of defects in agent-owned property?",
    answer: "Agents selling their own property must disclose ownership status and all known defects.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Failure to disclose can be treated as fraud under California law."
  },
  {
    id: 159,
    question: "What is Civil Code Section 2079?",
    answer: "Requires agents to conduct a competent visual inspection of residential property.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "hard",
    explanation: "Applies to 1–4 unit properties; agent must disclose observable defects to buyers."
  },
  {
    id: 160,
    question: "What are agency disclosure timing requirements?",
    answer: "Disclosure must occur before or at the same time as signing a listing or purchase agreement.",
    category: "Laws of Agency and Fiduciary Duties",
    difficulty: "medium",
    explanation: "Ensures all parties understand who represents whom to prevent undisclosed dual agency."
  },

  // Property Valuation and Financial Analysis (70 cards)
  {
    id: 161,
    question: "What is market value?",
    answer: "The most probable price a property would bring in an open and competitive market.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "easy",
    explanation: "Assumes buyer and seller are well-informed, acting without pressure, and the property is exposed to the market for a reasonable time."
  },
  {
    id: 162,
    question: "What is market price?",
    answer: "The actual price paid for a property.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "easy",
    explanation: "May differ from market value due to motivation, time pressure, or financing terms."
  },
  {
    id: 163,
    question: "What is cost?",
    answer: "The total amount spent to create or acquire a property.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "easy",
    explanation: "Includes land, labor, materials, and overhead. Not always equal to value or price."
  },
  {
    id: 164,
    question: "What is the principle of substitution?",
    answer: "A buyer will not pay more for a property than an equally desirable alternative.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Foundation of the Sales Comparison Approach in appraising real estate."
  },
  {
    id: 165,
    question: "What is the principle of supply and demand?",
    answer: "Value increases when supply is low and demand is high.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Market forces drive real estate prices; oversupply decreases property value."
  },
  {
    id: 166,
    question: "What is the principle of conformity?",
    answer: "Property value is maximized when it conforms to neighborhood standards.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "A luxury home in a modest area may be worth less due to lack of conformity."
  },
  {
    id: 167,
    question: "What is the principle of contribution?",
    answer: "An improvement adds value only to the extent it contributes to overall value.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Adding a $50,000 pool may not increase value by $50,000 if buyers don't desire it."
  },
  {
    id: 168,
    question: "What is the principle of highest and best use?",
    answer: "The most profitable, legally permissible, and feasible use of land.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "hard",
    explanation: "Determined through analysis of zoning, market demand, and physical characteristics."
  },
  {
    id: 169,
    question: "What is the principle of anticipation?",
    answer: "Value is based on expected future benefits of ownership.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Used heavily in the Income Approach; investors buy properties for future cash flow."
  },
  {
    id: 170,
    question: "What is the principle of change?",
    answer: "Real estate value is affected by economic and social changes.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Markets fluctuate due to interest rates, demographics, and infrastructure development."
  },
  {
    id: 171,
    question: "What is the principle of progression?",
    answer: "A lower-quality property benefits from being near higher-value homes.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "A small house in a luxury neighborhood may have an increased value."
  },
  {
    id: 172,
    question: "What is the principle of regression?",
    answer: "A higher-quality property loses value near lower-quality properties.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "A luxury home surrounded by older, poorly maintained homes may depreciate."
  },
  {
    id: 173,
    question: "What is the principle of competition?",
    answer: "Excess profits attract competition, which drives prices down.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Developers building similar projects in the same area can reduce overall profitability."
  },
  {
    id: 174,
    question: "What is the principle of balance?",
    answer: "Value is maximized when land and improvements are in optimal proportion.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Over-improvement leads to diminishing returns; under-improvement fails to utilize potential."
  },
  {
    id: 175,
    question: "What is the principle of contributing value?",
    answer: "Each part of a property adds value to the whole based on its contribution.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "easy",
    explanation: "Applies to landscaping, room additions, or renovations."
  },
  {
    id: 176,
    question: "What is an appraisal?",
    answer: "An unbiased estimate of property value by a licensed professional.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Used for loans, sales, taxation, and legal disputes. Must comply with USPAP standards."
  },
  {
    id: 177,
    question: "What is a Comparative Market Analysis (CMA)?",
    answer: "Broker's estimate of value based on recent comparable sales.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "easy",
    explanation: "Not an appraisal; used to set listing prices. Relies on local MLS data and market trends."
  },
  {
    id: 178,
    question: "What is the Sales Comparison Approach?",
    answer: "Appraisal method comparing similar properties recently sold.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Adjusts for differences in features, location, and condition. Common for residential properties."
  },
  {
    id: 179,
    question: "What is the Cost Approach?",
    answer: "Appraisal method based on the cost to replace improvements minus depreciation.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "hard",
    explanation: "Used for unique or new properties where comps are unavailable. Formula: Cost - Depreciation + Land Value."
  },
  {
    id: 180,
    question: "What is the Income Approach?",
    answer: "Appraisal method valuing property based on income it generates.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "hard",
    explanation: "Formula: Value = Net Operating Income ÷ Capitalization Rate (V = NOI / Cap Rate). Common for commercial properties."
  },
  {
    id: 181,
    question: "What is Gross Rent Multiplier (GRM)?",
    answer: "Ratio used to estimate value of income property.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Formula: Price ÷ Gross Rent = GRM. Quick screening tool for residential rentals."
  },
  {
    id: 182,
    question: "What is Capitalization Rate (Cap Rate)?",
    answer: "Rate of return based on income and value.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "hard",
    explanation: "Formula: NOI ÷ Value = Cap Rate. Higher risk properties generally have higher cap rates."
  },
  {
    id: 183,
    question: "What is Net Operating Income (NOI)?",
    answer: "Income after operating expenses but before taxes and debt service.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Formula: Gross Income - Operating Expenses = NOI. Key for income property valuation."
  },
  {
    id: 184,
    question: "What are operating expenses?",
    answer: "Costs necessary to maintain and operate property.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "easy",
    explanation: "Includes maintenance, insurance, management, and utilities but not mortgage payments or income tax."
  },
  {
    id: 185,
    question: "What is depreciation in appraisal?",
    answer: "Loss in value due to wear, age, or obsolescence.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Three types: physical deterioration, functional obsolescence, and external obsolescence."
  },
  {
    id: 186,
    question: "What is physical deterioration?",
    answer: "Loss of value from wear and tear or deferred maintenance.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "easy",
    explanation: "Examples: peeling paint, broken roof tiles. Often curable through repairs."
  },
  {
    id: 187,
    question: "What is functional obsolescence?",
    answer: "Loss in value due to outdated design or features.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Examples: one-bathroom home in a market expecting two; may be curable or incurable."
  },
  {
    id: 188,
    question: "What is external obsolescence?",
    answer: "Loss in value due to outside factors beyond property control.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "hard",
    explanation: "Examples: nearby airport noise or declining neighborhood. Usually incurable."
  },
  {
    id: 189,
    question: "What is reproduction cost?",
    answer: "Cost to build an exact duplicate using original materials and design.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "hard",
    explanation: "Used for historic property appraisals; generally more expensive than replacement cost."
  },
  {
    id: 190,
    question: "What is replacement cost?",
    answer: "Cost to build a similar structure using modern materials and standards.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Commonly used in the cost approach to determine value of new construction."
  },
  {
    id: 191,
    question: "What is economic life?",
    answer: "Period during which improvements contribute to value.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "When economic life ends, the building may still exist physically but has no value to the market."
  },
  {
    id: 192,
    question: "What is effective age?",
    answer: "The age a property appears to be, based on condition and updates.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "May differ from chronological age. A 20-year-old renovated home may have an effective age of 10."
  },
  {
    id: 193,
    question: "What is reconciliation in appraisal?",
    answer: "Final step in appraisal where appraiser weighs approaches to determine final value.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Involves judgment, not averaging. The most reliable approach receives the most weight."
  },
  {
    id: 194,
    question: "What is a competitive market?",
    answer: "A market where buyers and sellers have equal bargaining power.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Essential for accurate market value estimates. Government or distressed sales may distort true value."
  },
  {
    id: 195,
    question: "What are sales comparison adjustments?",
    answer: "Adjustments made to comparable sales to match the subject property.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "hard",
    explanation: "If a comparable has a better feature, subtract value; if it's inferior, add value. Adjustments make properties equivalent for comparison."
  },
  {
    id: 196,
    question: "What is an appraiser's final value opinion?",
    answer: "Appraiser's reconciled estimate of market value.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Result of analyzing all approaches (sales, cost, income) and determining the most appropriate for the property type."
  },
  {
    id: 197,
    question: "What is a Broker's Price Opinion (BPO)?",
    answer: "An estimate of value prepared by a licensed broker.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Used by lenders or asset managers; faster and cheaper than an appraisal but less formal."
  },
  {
    id: 198,
    question: "What is assessed value?",
    answer: "Value placed on property by the county assessor for tax purposes.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "In California, Proposition 13 limits assessed value increases to 2% annually unless ownership changes."
  },
  {
    id: 199,
    question: "What is Proposition 13?",
    answer: "California law limiting property tax rate and assessment increases.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Caps tax at 1% of assessed value and limits annual increase to 2%, except on sale or new construction."
  },
  {
    id: 200,
    question: "What is ad valorem tax?",
    answer: "Property tax based on assessed value.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "easy",
    explanation: "Means 'according to value.' Used to fund schools, roads, and local services."
  },
  {
    id: 201,
    question: "What is special assessment tax?",
    answer: "Tax for improvements benefiting specific properties.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Examples: sidewalks, sewers, lighting districts. Added to annual property tax bill."
  },
  {
    id: 202,
    question: "What is equalization rate?",
    answer: "Factor used to ensure tax assessments are uniform across regions.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "hard",
    explanation: "Used when different counties or municipalities assess property at varying percentages of market value."
  },
  {
    id: 203,
    question: "What is front foot value?",
    answer: "Valuation method using property frontage on a street or waterway.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Common for commercial and waterfront properties where frontage influences desirability."
  },
  {
    id: 204,
    question: "What is plottage value?",
    answer: "Increased value resulting from combining adjacent parcels under one ownership.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "The process is called assemblage. Example: combining lots to develop a shopping center."
  },
  {
    id: 205,
    question: "What is assemblage?",
    answer: "Combining adjoining parcels to increase overall value.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Often leads to plottage value. Used by developers for large-scale projects."
  },
  {
    id: 206,
    question: "What is overimprovement?",
    answer: "Improvement exceeding the property's maximum value contribution.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Occurs when the cost of improvement doesn't increase value proportionally. Example: luxury pool in a low-value area."
  },
  {
    id: 207,
    question: "What is underimprovement?",
    answer: "Property not utilizing its full potential value.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Example: vacant lot in a developed area. Represents opportunity for development."
  },
  {
    id: 208,
    question: "What is functional utility?",
    answer: "The usefulness or desirability of a property's design and layout.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Directly affects value. Poor floor plans or lack of amenities reduce functional utility."
  },
  {
    id: 209,
    question: "What is economic rent (market rent)?",
    answer: "Rent a property could command in an open market.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Determined by supply, demand, and comparable rental properties."
  },
  {
    id: 210,
    question: "What is contract rent?",
    answer: "Actual rent paid under a lease agreement.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "easy",
    explanation: "May be above or below market rent depending on lease terms and timing."
  },
  {
    id: 211,
    question: "What is Gross Income Multiplier (GIM)?",
    answer: "Valuation method comparing sale price to gross annual income.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Formula: Price ÷ Gross Income = GIM. Useful for small commercial properties."
  },
  {
    id: 212,
    question: "What is effective gross income?",
    answer: "Gross income minus vacancy and collection losses.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Reflects realistic income potential. Used to calculate NOI in income approach."
  },
  {
    id: 213,
    question: "What is cash flow?",
    answer: "Net income remaining after operating expenses and debt service.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Positive cash flow means income exceeds expenses; negative indicates loss."
  },
  {
    id: 214,
    question: "What is Return on Investment (ROI)?",
    answer: "Percentage of profit relative to investment cost.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Formula: Net Profit ÷ Total Investment = ROI. Used to measure investment performance."
  },
  {
    id: 215,
    question: "What is equity?",
    answer: "Owner's interest in property after debts are subtracted.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "easy",
    explanation: "Formula: Market Value - Loan Balance = Equity. Represents owner's actual wealth in the property."
  },
  {
    id: 216,
    question: "What is leverage?",
    answer: "Using borrowed funds to increase purchasing power.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Increases ROI potential but also risk. High leverage magnifies gains and losses."
  },
  {
    id: 217,
    question: "What is appreciation?",
    answer: "Increase in property value over time.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "easy",
    explanation: "Caused by inflation, demand, or improvements. Realized at sale when market value exceeds purchase price."
  },
  {
    id: 218,
    question: "What is depreciation for tax purposes?",
    answer: "Deduction allowing recovery of cost of income-producing property over time.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "hard",
    explanation: "Residential property depreciated over 27.5 years; commercial over 39 years under IRS rules."
  },
  {
    id: 219,
    question: "What is straight-line depreciation?",
    answer: "Method spreading depreciation evenly over an asset's useful life.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Annual depreciation = (Cost - Land Value) ÷ Useful Life."
  },
  {
    id: 220,
    question: "What is book value?",
    answer: "Value of property for accounting purposes after depreciation.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Not equal to market value; used for financial statements and tax filings."
  },
  {
    id: 221,
    question: "What is reversionary value?",
    answer: "Future value of property at the end of an investment period.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "hard",
    explanation: "Important in income capitalization and discounted cash flow analysis."
  },
  {
    id: 222,
    question: "What is discount rate?",
    answer: "Rate used to convert future income into present value.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "hard",
    explanation: "Reflects investor's required rate of return considering risk and time value of money."
  },
  {
    id: 223,
    question: "What is time value of money?",
    answer: "A dollar today is worth more than a dollar tomorrow.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Forms basis of discounting and compounding in investment analysis."
  },
  {
    id: 224,
    question: "What is present value?",
    answer: "Current worth of future income streams discounted at a specific rate.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "hard",
    explanation: "Used in real estate investment analysis to determine value of cash flows over time."
  },
  {
    id: 225,
    question: "What is future value?",
    answer: "Value of an investment at a future date after compounding interest.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Calculated with formula FV = PV × (1 + r)^n. Used to project investment growth."
  },
  {
    id: 226,
    question: "What is liquidity?",
    answer: "Ease of converting an asset into cash without loss of value.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Real estate is relatively illiquid compared to stocks or bonds due to transaction time and costs."
  },
  {
    id: 227,
    question: "What is the anticipation principle in income valuation?",
    answer: "Value is based on expected future benefits from income.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Key principle in the income approach; investors estimate value from projected cash flows."
  },
  {
    id: 228,
    question: "What is economic obsolescence?",
    answer: "Loss in property value due to external economic changes.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "hard",
    explanation: "Example: new freeway diverting traffic from a retail area or a factory closing nearby."
  },
  {
    id: 229,
    question: "What are appraisal report types?",
    answer: "Includes self-contained, summary, and restricted-use reports.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "medium",
    explanation: "Each type varies in detail; restricted-use reports are brief and intended for one client only."
  },
  {
    id: 230,
    question: "What is USPAP?",
    answer: "National standards for ethical and professional appraisal conduct.",
    category: "Property Valuation and Financial Analysis",
    difficulty: "hard",
    explanation: "Developed by The Appraisal Foundation; required by law for federally related transactions."
  },

  // Real Estate Calculations (25 cards)
  {
    id: 501,
    question: "A property sold for $450,000. The commission rate is 6%. What is the total commission?",
    answer: "$27,000",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "Total commission = Sales Price × Commission Rate (as a decimal). Step 1: Convert 6% to a decimal: 6% = 0.06. Step 2: Multiply the sales price by the commission rate: $450,000 × 0.06 = $27,000. The commission rate is always negotiable and is typically paid by the seller at closing. If the commission is split between listing and selling brokers, each would receive a portion of the $27,000."
  },
  {
    id: 502,
    question: "If a property is valued at $500,000 and a lender requires an 80% LTV, what is the loan amount?",
    answer: "$400,000",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "LTV (Loan-to-Value) ratio shows what percentage of the property value can be financed. Step 1: Convert 80% LTV to a decimal: 0.80. Step 2: Multiply the property value by the LTV ratio: $500,000 × 0.80 = $400,000. This means the lender will finance $400,000, and the borrower must provide a down payment of $100,000 (20% of $500,000)."
  },
  {
    id: 503,
    question: "A borrower is paying 2 discount points on a $300,000 loan. How much will the points cost?",
    answer: "$6,000",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "One point equals 1% of the loan amount. Step 1: Convert 2 points to a decimal: 2 points = 2% = 0.02. Step 2: Multiply the loan amount by the decimal: $300,000 × 0.02 = $6,000. Points are fees paid to the lender at closing, typically used to 'buy down' the interest rate (discount points) or as an origination fee. One point on a $300,000 loan = $3,000, so 2 points = $6,000."
  },
  {
    id: 504,
    question: "Annual property taxes are $4,200 and closing is on June 15th. The seller owned the property for 165 days. What is the seller's prorated share? (365-day year).",
    answer: "$1,900.27",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "Proration divides expenses between buyer and seller based on ownership days. California typically uses a 365-day year. Step 1: Calculate daily rate: $4,200 ÷ 365 = $11.51 per day. Step 2: The seller owned the property for 165 days. Step 3: Multiply daily rate by seller's days: $11.51 × 165 = $1,899.15, or using exact calculation: ($4,200 ÷ 365) × 165 = $1,900.27. The seller is debited (owes) $1,900.27, and the buyer is credited (receives) $1,900.27 since they'll be responsible for the remaining 200 days of the year."
  },
  {
    id: 505,
    question: "A property has an NOI of $60,000 and the market cap rate is 7.5%. What is the estimated value?",
    answer: "$800,000",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "The capitalization (cap) rate method estimates property value using the income approach. Formula: Value = NOI ÷ Cap Rate. NOI (Net Operating Income) is income after operating expenses but before debt service and taxes. Step 1: Convert 7.5% cap rate to a decimal: 0.075. Step 2: Divide NOI by cap rate: $60,000 ÷ 0.075 = $800,000. A higher cap rate indicates higher risk or lower value; a lower cap rate indicates lower risk or higher value. This method is commonly used for income-producing properties in California."
  },
  {
    id: 506,
    question: "A property has a gross annual rent of $72,000 and the area GRM is 12. What is the estimated value?",
    answer: "$864,000",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "GRM (Gross Rent Multiplier) is a simple valuation method for residential properties (1-4 units). Formula: Value = GRM × Gross Annual Rent. The GRM is found by comparing similar properties: GRM = Sales Price ÷ Gross Annual Rent. Step 1: Multiply the GRM by the gross annual rent: 12 × $72,000 = $864,000. This method is quick but less accurate than the cap rate method because it doesn't account for operating expenses. It's useful for quick estimates and comparing similar properties in the same California market area."
  },
  {
    id: 507,
    question: "How many square feet are in one acre?",
    answer: "43,560 square feet",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "One acre equals 43,560 square feet. This is a standard measurement that must be memorized for real estate exams. Useful conversions: 1 acre = 43,560 sq ft, 1 acre = 4,840 sq yards, 1 section = 640 acres, 1 township = 36 sections. To convert square feet to acres, divide by 43,560. Example: 87,120 sq ft ÷ 43,560 = 2 acres."
  },
  {
    id: 508,
    question: "A rectangular lot measures 180 feet by 240 feet. How many acres is it?",
    answer: "~0.99 acres",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "Step 1: Calculate the area in square feet: 180 feet × 240 feet = 43,200 square feet. Step 2: Convert square feet to acres by dividing by 43,560 (the number of square feet in one acre): 43,200 ÷ 43,560 = 0.9917 acres, which rounds to approximately 0.99 acres. Formula: Area (sq ft) ÷ 43,560 = Acres. This calculation is essential for determining lot sizes and property descriptions in California."
  },
  {
    id: 509,
    question: "A buyer's PITI is $2,400/mo. Annual taxes are $3,600 and insurance is $1,800. What is the monthly P&I?",
    answer: "$1,950",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "PITI stands for Principal, Interest, Taxes, and Insurance - the total monthly mortgage payment. To find P&I (Principal and Interest only), subtract the monthly Taxes and Insurance from PITI. Step 1: Calculate monthly Taxes and Insurance: ($3,600 + $1,800) ÷ 12 = $5,400 ÷ 12 = $450 per month. Step 2: Subtract monthly T&I from PITI: $2,400 - $450 = $1,950. So the monthly Principal and Interest payment is $1,950. This is the portion that goes toward paying down the loan and interest."
  },
  {
    id: 510,
    question: "A property's assessed value is $350,000. Under Proposition 13, what is the maximum annual property tax?",
    answer: "$3,500",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "Proposition 13 limits California property taxes to 1% of the assessed value, plus any local assessments. Step 1: Calculate 1% of assessed value: $350,000 × 0.01 = $3,500. This is the base property tax. Additional local assessments (for schools, bonds, etc.) may be added, but the base rate cannot exceed 1% of assessed value. Proposition 13 also limits annual assessment increases to 2% unless the property is sold or new construction occurs."
  },
  {
    id: 511,
    question: "A property was purchased in 2020 for $400,000. Under Proposition 13, what is the maximum assessed value in 2024?",
    answer: "$432,972.80",
    category: "Real Estate Calculations",
    difficulty: "hard",
    explanation: "Proposition 13 limits annual assessment increases to 2% per year. Step 1: Calculate the compounded increase over 4 years (2020 to 2024): $400,000 × (1.02)^4 = $400,000 × 1.082432 = $432,972.80. The 2% annual increase compounds each year. Year 1: $400,000 × 1.02 = $408,000. Year 2: $408,000 × 1.02 = $416,160. Year 3: $416,160 × 1.02 = $424,483.20. Year 4: $424,483.20 × 1.02 = $432,972.86. This protection continues as long as the property is not sold."
  },
  {
    id: 512,
    question: "A seller wants to net $250,000 after paying a 5% commission. What is the minimum selling price?",
    answer: "$263,157.89",
    category: "Real Estate Calculations",
    difficulty: "hard",
    explanation: "This is a reverse commission calculation. If the seller nets $250,000 after a 5% commission, the commission comes out of the sale price, so the seller keeps 95% (100% - 5%). Step 1: Calculate what percentage the seller keeps: 100% - 5% = 95%, or 0.95 as a decimal. Step 2: Divide the desired net amount by the percentage kept: $250,000 ÷ 0.95 = $263,157.89. To verify: $263,157.89 × 0.05 = $13,157.89 commission, leaving $263,157.89 - $13,157.89 = $250,000 net. Formula: Minimum Sale Price = Desired Net ÷ (1 - Commission Rate)."
  },
  {
    id: 513,
    question: "A loan of $350,000 has a monthly P&I payment of $2,100. The annual interest rate is 5.5%. How much of the first month's payment is interest?",
    answer: "$1,604.17",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "On an amortized loan, early payments consist mostly of interest. To find the first month's interest, calculate the annual interest and divide by 12. Step 1: Calculate annual interest: $350,000 × 0.055 (5.5% as decimal) = $19,250 per year. Step 2: Calculate monthly interest: $19,250 ÷ 12 = $1,604.17. The monthly P&I payment of $2,100 is not needed for this calculation - only the loan balance and interest rate matter. In the first month, $1,604.17 goes to interest and $495.83 goes to principal ($2,100 - $1,604.17 = $495.83). As the loan is paid down, the interest portion decreases and the principal portion increases."
  },
  {
    id: 514,
    question: "A property has PGI of $120,000, vacancy loss of 8%, and operating expenses of $45,000. What is the NOI?",
    answer: "$65,400",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "NOI (Net Operating Income) is calculated from PGI (Potential Gross Income) by accounting for vacancies and operating expenses. Step 1: Calculate EGI (Effective Gross Income): PGI minus vacancy loss. Vacancy loss = $120,000 × 0.08 = $9,600. EGI = $120,000 - $9,600 = $110,400. Alternatively: EGI = PGI × (1 - Vacancy Rate) = $120,000 × 0.92 = $110,400. Step 2: Calculate NOI: EGI minus Operating Expenses = $110,400 - $45,000 = $65,400. NOI is the income remaining after all operating expenses but before debt service (mortgage payments) and income taxes. It's a key metric for income-producing properties."
  },
  {
    id: 515,
    question: "A property's market value is $600,000. The owner has a loan balance of $420,000. What is the owner's equity?",
    answer: "$180,000",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "Equity represents the owner's actual interest in the property after subtracting debts. Formula: Equity = Market Value - Loan Balance. Step 1: Subtract the loan balance from the market value: $600,000 - $420,000 = $180,000. This is the amount the owner would receive if they sold the property and paid off the loan. Equity increases as the loan is paid down (amortization) or as the property value increases (appreciation)."
  },
  {
    id: 516,
    question: "An investor buys a property for $800,000 with a 25% down payment. The property NOI is $56,000 and annual debt service is $36,000. What is the cash-on-cash return?",
    answer: "10%",
    category: "Real Estate Calculations",
    difficulty: "hard",
    explanation: "Cash-on-cash return measures the annual return on the actual cash invested (down payment). Formula: Cash-on-Cash Return = Annual Cash Flow ÷ Cash Invested. Step 1: Calculate the down payment (cash invested): $800,000 × 0.25 = $200,000. Step 2: Calculate annual cash flow: NOI - Debt Service = $56,000 - $36,000 = $20,000. Step 3: Divide cash flow by cash invested: $20,000 ÷ $200,000 = 0.10 = 10%. This metric shows the return on the investor's actual cash investment, not the total property value. It's a key metric for evaluating investment properties in California."
  },
  {
    id: 517,
    question: "A rectangular office space is 50 feet by 80 feet. Annual rent is $24 per sq ft. What is the monthly rent?",
    answer: "$8,000",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "Step 1: Calculate the area in square feet: 50 feet × 80 feet = 4,000 square feet. Step 2: Calculate annual rent: 4,000 sq ft × $24 per sq ft = $96,000 per year. Step 3: Convert annual rent to monthly rent: $96,000 ÷ 12 months = $8,000 per month. Commercial leases in California often quote rent per square foot per year, so you must calculate the total annual rent first, then divide by 12 to get the monthly amount."
  },
  {
    id: 518,
    question: "A property sold for $525,000. In Los Angeles County, the transfer tax is $0.55 per $500 (or fraction). What is the transfer tax?",
    answer: "$577.50",
    category: "Real Estate Calculations",
    difficulty: "hard",
    explanation: "California transfer taxes vary by county and city. Los Angeles County charges $0.55 per $500 (or fraction) of the sales price. Step 1: Round UP to the nearest $500: $525,000 is already a multiple of $500, so no rounding needed. Step 2: Divide by $500: $525,000 ÷ $500 = 1,050. Step 3: Multiply by $0.55: 1,050 × $0.55 = $577.50. Note: If the sale price had been $525,100, you would round up to $525,500 before calculating. Transfer taxes are typically paid by the seller at closing and vary significantly by California county and city."
  },
  {
    id: 519,
    question: "How many linear feet of fencing are needed to enclose a rectangular lot that is 150 feet by 200 feet?",
    answer: "700 feet",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "To find the perimeter (total length of fencing needed), use the formula: Perimeter = 2 × (Length + Width). Step 1: Add the length and width: 150 feet + 200 feet = 350 feet. Step 2: Multiply by 2: 2 × 350 = 700 feet. This gives the total linear feet of fencing needed to enclose the rectangular lot. Linear feet means the total length in a straight line, which is what you need for fencing calculations."
  },
  {
    id: 520,
    question: "A commission of $18,000 was paid on a sale. The commission rate was 6%. What was the sales price?",
    answer: "$300,000",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "This is a reverse commission calculation - finding the sales price when you know the commission amount and rate. Formula: Sales Price = Commission ÷ Commission Rate (as decimal). Step 1: Convert 6% to a decimal: 6% = 0.06. Step 2: Divide the commission by the commission rate: $18,000 ÷ 0.06 = $300,000. To verify: $300,000 × 0.06 = $18,000. This type of calculation is useful when you know the commission paid and need to determine what the property sold for."
  },
  {
    id: 521,
    question: "A property has an assessed value of $280,000. Under Proposition 13, the tax rate is 1%. What are the annual property taxes?",
    answer: "$2,800",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "Proposition 13 limits California property taxes to 1% of the assessed value, plus any local assessments. Step 1: Calculate 1% of assessed value: $280,000 × 0.01 = $2,800. This is the base property tax. Additional local assessments (for schools, bonds, special districts, etc.) may be added on top of this base amount, but the base rate cannot exceed 1% of assessed value. The total tax bill may be higher due to these additional assessments."
  },
  {
    id: 522,
    question: "A property sold for $380,000, which was 95% of the listing price. What was the listing price?",
    answer: "$400,000",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "This is a reverse percentage calculation. If the sale price is 95% of the listing price, then the listing price is the sale price divided by 0.95. Step 1: Convert 95% to a decimal: 0.95. Step 2: Divide the sale price by the percentage (as a decimal): $380,000 ÷ 0.95 = $400,000. To verify: $400,000 × 0.95 = $380,000. This type of calculation is useful when you know the final amount and need to find the original amount before a percentage reduction or increase."
  },
  {
    id: 523,
    question: "An investor purchased a property for $600,000 and sold it 3 years later for $720,000. What is the total appreciation percentage?",
    answer: "20%",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "Appreciation is the increase in property value over time. Formula: Appreciation % = [(Sale Price - Purchase Price) ÷ Purchase Price] × 100. Step 1: Calculate the dollar amount of appreciation: $720,000 - $600,000 = $120,000. Step 2: Divide by the purchase price: $120,000 ÷ $600,000 = 0.20. Step 3: Convert to percentage: 0.20 × 100 = 20%. This represents a 20% total appreciation over the 3-year period. To find annual appreciation, divide by the number of years, but this question asks for total appreciation."
  },
  {
    id: 524,
    question: "A property has a market value of $450,000 and is assessed at 100% of market value. The tax rate is 1.2% (including local assessments). What are the annual property taxes?",
    answer: "$5,400",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "In California, assessed value is typically the purchase price (for new purchases) or the Prop 13 adjusted value (for existing owners). Step 1: Calculate assessed value: $450,000 × 1.00 = $450,000 (assessed at 100% of market value). Step 2: Calculate property tax: $450,000 × 0.012 (1.2% as decimal) = $5,400. Note: While Proposition 13 limits the base rate to 1%, additional local assessments can bring the total rate above 1%. The 1.2% rate includes both the base 1% and additional local assessments."
  },
  {
    id: 525,
    question: "A buyer obtains a $400,000 loan with a 4.5% interest rate. What is the annual interest?",
    answer: "$18,000",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "Annual interest is calculated by multiplying the loan amount by the annual interest rate (as a decimal). Step 1: Convert 4.5% to a decimal: 4.5% = 0.045. Step 2: Multiply loan amount by interest rate: $400,000 × 0.045 = $18,000. This is the total interest that would be paid in one year if the loan balance remained constant. On an amortized loan, the interest portion of each payment decreases over time as the principal is paid down."
  },
  {
    id: 526,
    question: "What is the formula for the area of a triangle?",
    answer: "Area = (1/2) × Base × Height",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "The area of a triangle is calculated as: Area = (1/2) × Base × Height, or Area = Base × Height ÷ 2. The base and height must be perpendicular to each other. This formula is used for calculating the area of irregularly shaped lots that can be divided into triangular sections, or for triangular parcels. Example: A triangle with a base of 120 feet and height of 90 feet has an area of (1/2) × 120 × 90 = 5,400 square feet."
  },
  {
    id: 527,
    question: "A property has an NOI of $48,000. If the cap rate is 6%, what is the estimated value?",
    answer: "$800,000",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "The capitalization (cap) rate method estimates property value using the income approach. Formula: Value = NOI ÷ Cap Rate. Step 1: Convert 6% cap rate to a decimal: 0.06. Step 2: Divide NOI by cap rate: $48,000 ÷ 0.06 = $800,000. A higher cap rate indicates higher risk or lower value; a lower cap rate indicates lower risk or higher value. This method is commonly used for income-producing properties in California, especially for commercial real estate investments."
  },
  {
    id: 528,
    question: "How many acres is 1/4 of a section?",
    answer: "160 acres",
    category: "Real Estate Calculations",
    difficulty: "medium",
    explanation: "A section is a standard unit of land measurement in the U.S. Public Land Survey System. One full section equals 640 acres. To find 1/4 of a section: 640 acres ÷ 4 = 160 acres. Alternatively: 640 × 0.25 = 160 acres. A quarter section (1/4 section) is also called a 'quarter' and is a common land division. Other common divisions: 1/2 section = 320 acres, 1/8 section (half of a quarter) = 80 acres. This measurement system is used in California and other western states."
  },
  {
    id: 529,
    question: "A property's after-tax cash flow is $15,000 and the initial equity was $120,000. What is the after-tax return on equity?",
    answer: "12.5%",
    category: "Real Estate Calculations",
    difficulty: "hard",
    explanation: "After-tax return on equity measures the percentage return on the investor's equity investment after all expenses and taxes. Formula: Return on Equity = After-Tax Cash Flow ÷ Initial Equity. Step 1: Divide after-tax cash flow by initial equity: $15,000 ÷ $120,000 = 0.125. Step 2: Convert to percentage: 0.125 × 100 = 12.5%. This metric shows how well the investor's equity is performing. After-tax cash flow is the money remaining after all operating expenses, debt service (mortgage payments), and income taxes. Initial equity is the down payment plus any additional capital invested."
  },
  {
    id: 530,
    question: "A rectangular lot measures 100 feet by 200 feet. How many square feet is it?",
    answer: "20,000 square feet",
    category: "Real Estate Calculations",
    difficulty: "easy",
    explanation: "To find the area of a rectangle, multiply length by width. Step 1: Multiply the dimensions: 100 feet × 200 feet = 20,000 square feet. This is a basic area calculation used frequently in real estate for determining lot sizes, building footprints, and property descriptions. To convert to acres: 20,000 ÷ 43,560 = approximately 0.46 acres."
  },

  // Financing (45 cards)
  {
    id: 231,
    question: "What is a mortgage?",
    answer: "A loan secured by real property.",
    category: "Financing",
    difficulty: "easy",
    explanation: "The borrower (mortgagor) pledges property as collateral to the lender (mortgagee)."
  },
  {
    id: 232,
    question: "What is a promissory note?",
    answer: "Written promise to repay a loan.",
    category: "Financing",
    difficulty: "easy",
    explanation: "Contains loan amount, interest rate, payment schedule, and terms. Personal obligation of the borrower."
  },
  {
    id: 233,
    question: "What is a deed of trust?",
    answer: "Three-party security instrument used in California.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Involves borrower (trustor), lender (beneficiary), and neutral third party (trustee). Trustee holds title until loan is paid."
  },
  {
    id: 234,
    question: "What is a mortgage deed?",
    answer: "Two-party security instrument used in some states.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Direct agreement between borrower and lender. Lender holds lien on property until loan is satisfied."
  },
  {
    id: 235,
    question: "What is principal?",
    answer: "The original amount borrowed.",
    category: "Financing",
    difficulty: "easy",
    explanation: "Does not include interest or fees. Principal balance decreases with each payment."
  },
  {
    id: 236,
    question: "What is interest?",
    answer: "Cost of borrowing money, expressed as a percentage.",
    category: "Financing",
    difficulty: "easy",
    explanation: "Can be fixed (constant) or variable (adjusts with market rates)."
  },
  {
    id: 237,
    question: "What is a fixed-rate mortgage?",
    answer: "Loan with constant interest rate for the entire term.",
    category: "Financing",
    difficulty: "easy",
    explanation: "Payment amount remains the same; provides stability and predictability."
  },
  {
    id: 238,
    question: "What is an adjustable-rate mortgage (ARM)?",
    answer: "Loan with interest rate that changes periodically.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Rate adjusts based on index plus margin. Payment amounts can increase or decrease."
  },
  {
    id: 239,
    question: "What is an index?",
    answer: "Published rate used to determine ARM adjustments.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Examples: Treasury rates, LIBOR, or COFI. Index plus margin equals new interest rate."
  },
  {
    id: 240,
    question: "What is a margin?",
    answer: "Fixed amount added to index rate for ARM loans.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Set by lender at origination; remains constant throughout loan term."
  },
  {
    id: 241,
    question: "What is a cap?",
    answer: "Limit on how much ARM rate or payment can change.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Includes periodic caps (per adjustment) and lifetime caps (total increase)."
  },
  {
    id: 242,
    question: "What is a teaser rate?",
    answer: "Initial low rate on ARM that increases after introductory period.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Used to attract borrowers; rate rises to index plus margin after initial period."
  },
  {
    id: 243,
    question: "What is a balloon payment?",
    answer: "Large final payment due at loan maturity.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Common in short-term loans; borrower must refinance or sell to pay balance."
  },
  {
    id: 244,
    question: "What is a conventional loan?",
    answer: "Loan not insured or guaranteed by government.",
    category: "Financing",
    difficulty: "easy",
    explanation: "Requires higher down payment and credit score than government-backed loans."
  },
  {
    id: 245,
    question: "What is an FHA loan?",
    answer: "Loan insured by Federal Housing Administration.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Allows lower down payment (3.5%) and credit scores. Requires mortgage insurance."
  },
  {
    id: 246,
    question: "What is a VA loan?",
    answer: "Loan guaranteed by Department of Veterans Affairs.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Available to eligible veterans; requires no down payment or mortgage insurance."
  },
  {
    id: 247,
    question: "What is a USDA loan?",
    answer: "Loan guaranteed by U.S. Department of Agriculture.",
    category: "Financing",
    difficulty: "medium",
    explanation: "For rural properties; allows 100% financing with income limits."
  },
  {
    id: 248,
    question: "What is private mortgage insurance (PMI)?",
    answer: "Insurance protecting lender against borrower default.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Required on conventional loans with less than 20% down payment."
  },
  {
    id: 249,
    question: "What is mortgage insurance premium (MIP)?",
    answer: "Insurance required on FHA loans.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Includes upfront premium and annual premium. May be required for life of loan."
  },
  {
    id: 250,
    question: "What is a jumbo loan?",
    answer: "Loan exceeding conforming loan limits.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Higher interest rates and stricter requirements than conforming loans."
  },
  {
    id: 251,
    question: "What is a conforming loan?",
    answer: "Loan meeting Fannie Mae and Freddie Mac guidelines.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Easier to sell to investors; typically offers lower interest rates."
  },
  {
    id: 252,
    question: "What is a loan-to-value ratio (LTV)?",
    answer: "Percentage of loan amount relative to property value.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Formula: Loan Amount ÷ Property Value = LTV. Higher LTV means higher risk."
  },
  {
    id: 253,
    question: "What is a debt-to-income ratio (DTI)?",
    answer: "Percentage of monthly income used for debt payments.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Includes mortgage, credit cards, and other loans. Lower DTI improves loan approval chances."
  },
  {
    id: 254,
    question: "What is a credit score?",
    answer: "Numerical rating of borrower's creditworthiness.",
    category: "Financing",
    difficulty: "easy",
    explanation: "Based on payment history, debt levels, and credit history length. Higher scores get better rates."
  },
  {
    id: 255,
    question: "What is a pre-approval?",
    answer: "Lender's preliminary commitment to make a loan.",
    category: "Financing",
    difficulty: "easy",
    explanation: "Based on credit check and income verification. Strengthens buyer's offer."
  },
  {
    id: 256,
    question: "What is a pre-qualification?",
    answer: "Informal estimate of loan amount based on basic information.",
    category: "Financing",
    difficulty: "easy",
    explanation: "No credit check required; less reliable than pre-approval."
  },
  {
    id: 257,
    question: "What is a loan estimate?",
    answer: "Form disclosing loan terms and closing costs.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Required within 3 days of loan application. Helps borrowers compare offers."
  },
  {
    id: 258,
    question: "What is a closing disclosure?",
    answer: "Final form showing actual loan terms and costs.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Must be provided 3 days before closing. Allows borrower to verify final terms."
  },
  {
    id: 259,
    question: "What is an escrow account?",
    answer: "Account holding funds for property taxes and insurance.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Lender collects monthly payments to cover annual expenses. Ensures timely payment."
  },
  {
    id: 260,
    question: "What is a loan origination fee?",
    answer: "Fee charged by lender for processing the loan.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Usually 1% of loan amount. May be negotiable or waived in competitive markets."
  },
  {
    id: 261,
    question: "What are discount points?",
    answer: "Fees paid to reduce interest rate.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Each point equals 1% of loan amount. May be tax-deductible."
  },
  {
    id: 262,
    question: "What is a prepayment penalty?",
    answer: "Fee charged for paying off loan early.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Protects lender from lost interest. May apply for first few years of loan."
  },
  {
    id: 263,
    question: "What is a lock-in period?",
    answer: "Time during which interest rate is guaranteed.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Protects borrower from rate increases during loan processing. Usually 30-60 days."
  },
  {
    id: 264,
    question: "What is a rate lock fee?",
    answer: "Fee charged to guarantee interest rate.",
    category: "Financing",
    difficulty: "medium",
    explanation: "May be refunded at closing or applied to closing costs."
  },
  {
    id: 265,
    question: "What is a subprime loan?",
    answer: "Loan for borrowers with poor credit or high risk.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Higher interest rates and fees than prime loans. Stricter regulations after 2008 crisis."
  },
  {
    id: 266,
    question: "What is a hard money loan?",
    answer: "Short-term loan secured by real estate.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Used by investors for quick purchases or renovations. Higher rates, shorter terms."
  },
  {
    id: 267,
    question: "What is seller financing?",
    answer: "Property seller provides loan to buyer.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Seller acts as lender; buyer makes payments directly to seller."
  },
  {
    id: 268,
    question: "What is a wraparound loan?",
    answer: "New loan that includes existing mortgage balance.",
    category: "Financing",
    difficulty: "hard",
    explanation: "Buyer makes payments to seller, who continues paying original lender. Seller keeps difference."
  },
  {
    id: 269,
    question: "What is a land contract?",
    answer: "Seller-financed agreement where title transfers after final payment.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Buyer gains equitable title; seller retains legal title as security."
  },
  {
    id: 270,
    question: "What is a lease option?",
    answer: "Agreement allowing tenant to purchase property at set price.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Part of rent may apply to purchase price. Tenant has right but not obligation to buy."
  },
  {
    id: 271,
    question: "What is a lease purchase?",
    answer: "Agreement requiring tenant to purchase property.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Binding contract; tenant must complete purchase or face penalties."
  },
  {
    id: 272,
    question: "What is a reverse mortgage?",
    answer: "Loan allowing seniors to convert home equity to cash.",
    category: "Financing",
    difficulty: "medium",
    explanation: "No monthly payments required; loan repaid when borrower moves or dies."
  },
  {
    id: 273,
    question: "What is a home equity loan?",
    answer: "Second mortgage with fixed payments.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Borrower receives lump sum; makes fixed monthly payments over set term."
  },
  {
    id: 274,
    question: "What is a home equity line of credit (HELOC)?",
    answer: "Revolving credit line secured by home equity.",
    category: "Financing",
    difficulty: "medium",
    explanation: "Borrower can draw funds as needed; typically variable interest rate."
  },
  {
    id: 275,
    question: "What is refinancing?",
    answer: "Replacing existing loan with new loan.",
    category: "Financing",
    difficulty: "easy",
    explanation: "Done to lower rate, change terms, or cash out equity."
  },

  // Transfer of Property (40 cards)
  {
    id: 276,
    question: "What is a deed?",
    answer: "Legal document transferring ownership of real property.",
    category: "Transfer of Property",
    difficulty: "easy",
    explanation: "Must be in writing, signed by grantor, and delivered to grantee to be valid."
  },
  {
    id: 277,
    question: "What is a grantor?",
    answer: "Person transferring property ownership.",
    category: "Transfer of Property",
    difficulty: "easy",
    explanation: "The seller or person giving up title to the property."
  },
  {
    id: 278,
    question: "What is a grantee?",
    answer: "Person receiving property ownership.",
    category: "Transfer of Property",
    difficulty: "easy",
    explanation: "The buyer or person acquiring title to the property."
  },
  {
    id: 279,
    question: "What is a warranty deed?",
    answer: "Deed providing strongest protection to buyer.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Grantor warrants clear title and defends against all claims. Most common in California."
  },
  {
    id: 280,
    question: "What is a quitclaim deed?",
    answer: "Deed transferring only grantor's interest without warranties.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Used to clear title defects or transfer between family members. No protection for grantee."
  },
  {
    id: 281,
    question: "What is a special warranty deed?",
    answer: "Deed warranting title only during grantor's ownership period.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Less protection than general warranty deed; grantor only defends against claims arising during their ownership."
  },
  {
    id: 282,
    question: "What is a bargain and sale deed?",
    answer: "Deed transferring property without warranties.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Common in foreclosure sales; grantor transfers interest but makes no promises about title quality."
  },
  {
    id: 283,
    question: "What is a gift deed?",
    answer: "Deed transferring property without consideration.",
    category: "Transfer of Property",
    difficulty: "easy",
    explanation: "Used for family transfers or charitable donations. May have tax implications."
  },
  {
    id: 284,
    question: "What is a deed of trust?",
    answer: "Security instrument used in California for loans.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Three-party instrument: borrower (trustor), lender (beneficiary), and trustee. Trustee holds title until loan is paid."
  },
  {
    id: 285,
    question: "What is a reconveyance deed?",
    answer: "Deed releasing property from deed of trust.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Issued by trustee when loan is paid off, removing the lien from the property."
  },
  {
    id: 286,
    question: "What is a sheriff's deed?",
    answer: "Deed issued after foreclosure sale.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Transfers property to highest bidder at auction. May have redemption rights."
  },
  {
    id: 287,
    question: "What is a trustee's deed?",
    answer: "Deed issued after non-judicial foreclosure.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Used in California for foreclosures under deed of trust. Faster than judicial foreclosure."
  },
  {
    id: 288,
    question: "What is a tax deed?",
    answer: "Deed issued after tax foreclosure sale.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Transfers property to purchaser at tax auction. May have redemption period."
  },
  {
    id: 289,
    question: "What is a probate deed?",
    answer: "Deed transferring property from deceased person's estate.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Issued by executor or administrator after court approval. May require probate court confirmation."
  },
  {
    id: 290,
    question: "What is a partition deed?",
    answer: "Deed dividing jointly owned property.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Used when co-owners want to separate their interests. May be voluntary or court-ordered."
  },
  {
    id: 291,
    question: "What is a correction deed?",
    answer: "Deed correcting errors in previous deed.",
    category: "Transfer of Property",
    difficulty: "easy",
    explanation: "Used to fix misspellings, incorrect legal descriptions, or other mistakes."
  },
  {
    id: 292,
    question: "What is a deed of release?",
    answer: "Document releasing property from lien or encumbrance.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Used to remove mechanics liens, judgments, or other claims from title."
  },
  {
    id: 293,
    question: "What is a deed of easement?",
    answer: "Deed granting easement rights to another party.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Creates permanent right to use property for specific purpose, such as access or utilities."
  },
  {
    id: 294,
    question: "What is a deed restriction?",
    answer: "Limitation on property use included in deed.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Runs with the land; binds all future owners. Example: limiting property to residential use."
  },
  {
    id: 295,
    question: "What is delivery of deed?",
    answer: "Transferring possession of deed to grantee.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Essential for deed validity; grantor must intend to transfer ownership immediately."
  },
  {
    id: 296,
    question: "What is acceptance of deed?",
    answer: "Grantee's agreement to receive property ownership.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Usually presumed if grantee benefits from the transfer. Can be express or implied."
  },
  {
    id: 297,
    question: "What is recording of deed?",
    answer: "Filing deed with county recorder's office.",
    category: "Transfer of Property",
    difficulty: "easy",
    explanation: "Provides public notice of ownership change. Not required for validity but essential for protection."
  },
  {
    id: 298,
    question: "What is constructive notice?",
    answer: "Legal presumption that recorded documents are known to all.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Anyone dealing with property is deemed to know about recorded liens, easements, and restrictions."
  },
  {
    id: 299,
    question: "What is actual notice?",
    answer: "Direct knowledge of facts affecting property.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Person has been informed or observed conditions that would affect property value or use."
  },
  {
    id: 300,
    question: "What is inquiry notice?",
    answer: "Duty to investigate suspicious circumstances.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "If facts suggest problems, buyer must investigate further. Cannot ignore obvious issues."
  },
  {
    id: 301,
    question: "What is a title search?",
    answer: "Examination of public records to determine ownership history.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Reveals liens, easements, and other encumbrances. Essential for title insurance."
  },
  {
    id: 302,
    question: "What is title insurance?",
    answer: "Insurance protecting against title defects.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Covers unknown liens, fraud, or errors in public records. Required by most lenders."
  },
  {
    id: 303,
    question: "What is a title report?",
    answer: "Document summarizing title search findings.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Lists all liens, easements, and restrictions affecting the property."
  },
  {
    id: 304,
    question: "What is a preliminary title report?",
    answer: "Initial title search before closing.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Shows current title status and conditions. Used to identify issues before closing."
  },
  {
    id: 305,
    question: "What is a title commitment?",
    answer: "Promise to issue title insurance after closing.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Lists requirements that must be met before policy is issued."
  },
  {
    id: 306,
    question: "What is a title policy?",
    answer: "Insurance policy protecting against title defects.",
    category: "Transfer of Property",
    difficulty: "easy",
    explanation: "Issued after closing; covers owner and lender against unknown title problems."
  },
  {
    id: 307,
    question: "What is a chain of title?",
    answer: "Sequence of ownership transfers for a property.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Shows how property passed from one owner to another over time."
  },
  {
    id: 308,
    question: "What is a cloud on title?",
    answer: "Any claim or encumbrance affecting clear ownership.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Must be resolved before clear title can be transferred. Examples: liens, easements, or disputes."
  },
  {
    id: 309,
    question: "What is quiet title action?",
    answer: "Lawsuit to establish clear ownership.",
    category: "Transfer of Property",
    difficulty: "hard",
    explanation: "Used to resolve title disputes or remove invalid claims. Court determines rightful owner."
  },
  {
    id: 310,
    question: "What is adverse possession?",
    answer: "Acquiring ownership through continuous, hostile use.",
    category: "Transfer of Property",
    difficulty: "hard",
    explanation: "Requires open, notorious, continuous possession for statutory period (5 years in California)."
  },
  {
    id: 311,
    question: "What is eminent domain?",
    answer: "Government's right to take private property for public use.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Requires just compensation and public purpose. Process called condemnation."
  },
  {
    id: 312,
    question: "What is condemnation?",
    answer: "Legal process of taking property through eminent domain.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Government must prove public purpose and pay fair market value."
  },
  {
    id: 313,
    question: "What is inverse condemnation?",
    answer: "Property owner's lawsuit against government for taking property.",
    category: "Transfer of Property",
    difficulty: "hard",
    explanation: "Filed when government actions effectively take property value without formal condemnation."
  },
  {
    id: 314,
    question: "What is dedication?",
    answer: "Voluntary transfer of land for public use.",
    category: "Transfer of Property",
    difficulty: "medium",
    explanation: "Developers often dedicate streets or parks to municipalities during subdivision approval."
  },
  {
    id: 315,
    question: "What is escheat?",
    answer: "Reversion of unclaimed property to the state.",
    category: "Transfer of Property",
    difficulty: "easy",
    explanation: "Occurs when person dies intestate without heirs. Ensures property ownership continuity."
  },

  // Practice of Real Estate and Mandated Disclosures (125 cards)
  {
    id: 316,
    question: "What is the Real Estate Commissioner?",
    answer: "Head of the California Department of Real Estate (DRE).",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Appointed by the Governor to enforce real estate laws, regulate licensees, and protect the public."
  },
  {
    id: 317,
    question: "What is the California Department of Real Estate (DRE)?",
    answer: "State agency that licenses and regulates real estate professionals.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Oversees licensing, education, investigations, and enforcement of California Real Estate Law."
  },
  {
    id: 318,
    question: "What is the Real Estate Law (Business & Professions Code 10000-11288)?",
    answer: "Primary body of law regulating real estate licensing and conduct in California.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Establishes requirements for licensing, advertising, and disciplinary actions."
  },
  {
    id: 319,
    question: "What are DRE License Types?",
    answer: "Includes salesperson, broker, and restricted licenses.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Salespersons must work under a broker; brokers may operate independently or employ agents."
  },
  {
    id: 320,
    question: "What is a Restricted License?",
    answer: "Conditional license issued after disciplinary action.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Allows practice under strict conditions; may be revoked for further violations."
  },
  {
    id: 321,
    question: "What is Broker Supervision Requirement?",
    answer: "Brokers must actively supervise associated salespersons.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Includes reviewing contracts, maintaining records, and ensuring compliance with real estate laws."
  },
  {
    id: 322,
    question: "What is License Renewal Requirement?",
    answer: "California licenses expire every four years.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Renewal requires 45 hours of continuing education including ethics, agency, fair housing, trust fund handling, and risk management."
  },
  {
    id: 323,
    question: "What is Continuing Education Requirement?",
    answer: "45 hours of education required for license renewal.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Must include ethics, agency, fair housing, trust fund handling, and risk management courses."
  },
  {
    id: 324,
    question: "What is the Transfer Disclosure Statement (TDS)?",
    answer: "Required disclosure form for residential property sales.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Mandatory for 1-4 unit residential properties; discloses known defects and conditions."
  },
  {
    id: 325,
    question: "What is Natural Hazard Disclosure (NHD)?",
    answer: "Report disclosing natural hazard zones affecting property.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Required for residential sales; includes flood, fire, earthquake, and seismic zones."
  },
  {
    id: 326,
    question: "What is Lead-Based Paint Disclosure?",
    answer: "Federal requirement for properties built before 1978.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Must disclose known lead-based paint hazards and provide EPA pamphlet to buyers."
  },
  {
    id: 327,
    question: "What is Megan's Law Disclosure?",
    answer: "California requirement to disclose registered sex offenders.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Must inform buyers about Megan's Law website and registered sex offender database."
  },
  {
    id: 328,
    question: "What is the Agency Disclosure Form?",
    answer: "Required form explaining agency relationships in transactions.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Must be provided before or at time of signing listing or purchase agreement."
  },
  {
    id: 329,
    question: "What is Dual Agency Disclosure?",
    answer: "Required disclosure when broker represents both buyer and seller.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Must be disclosed immediately and confirmed in writing with informed consent from both parties."
  },
  {
    id: 330,
    question: "What is Material Fact Disclosure?",
    answer: "Agent's duty to disclose facts affecting property value or desirability.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Includes physical defects, legal issues, and neighborhood conditions that could influence decisions."
  },
  {
    id: 331,
    question: "What is Civil Code Section 2079?",
    answer: "Requires agents to conduct competent visual inspection of residential property.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Applies to 1-4 unit properties; agent must disclose observable defects to buyers."
  },
  {
    id: 332,
    question: "What is the Fair Housing Act?",
    answer: "Federal law prohibiting discrimination in housing.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Protects against discrimination based on race, color, religion, sex, national origin, familial status, or disability."
  },
  {
    id: 333,
    question: "What is the California Fair Employment and Housing Act (FEHA)?",
    answer: "State law providing broader fair housing protections.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Includes additional protected classes like sexual orientation, gender identity, and source of income."
  },
  {
    id: 334,
    question: "What is Steering?",
    answer: "Directing buyers to or away from certain neighborhoods based on protected characteristics.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Illegal practice that limits housing choices based on race, religion, or other protected classes."
  },
  {
    id: 335,
    question: "What is Blockbusting?",
    answer: "Encouraging homeowners to sell by claiming neighborhood is changing.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Illegal practice that exploits fears about neighborhood demographic changes."
  },
  {
    id: 336,
    question: "What is Redlining?",
    answer: "Denying services or loans based on neighborhood demographics.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Historically used to exclude minority neighborhoods from financial services and real estate opportunities."
  },
  {
    id: 337,
    question: "What is Reasonable Accommodation?",
    answer: "Modification to rules or services for disabled persons.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Required under fair housing law; examples include allowing service animals or wheelchair ramps."
  },
  {
    id: 338,
    question: "What is Reasonable Modification?",
    answer: "Physical changes to property for disabled persons.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Tenant may make modifications at their own expense; landlord cannot refuse reasonable requests."
  },
  {
    id: 339,
    question: "What is Familial Status Protection?",
    answer: "Protection against discrimination based on family composition.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Prohibits discrimination against families with children under 18, pregnant women, or persons seeking custody."
  },
  {
    id: 340,
    question: "What is Disability Protection?",
    answer: "Protection against discrimination based on physical or mental disability.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Includes protection for persons with AIDS/HIV, mental illness, and physical disabilities."
  },
  {
    id: 341,
    question: "What is Source of Income Protection?",
    answer: "California protection against discrimination based on income source.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Prohibits discrimination against tenants using Section 8 vouchers or other government assistance."
  },
  {
    id: 342,
    question: "What is Sexual Orientation Protection?",
    answer: "California protection against discrimination based on sexual orientation.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Prohibits discrimination against lesbian, gay, bisexual, and transgender persons."
  },
  {
    id: 343,
    question: "What is Gender Identity Protection?",
    answer: "California protection against discrimination based on gender identity.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Prohibits discrimination against transgender persons and those whose gender identity differs from birth sex."
  },
  {
    id: 344,
    question: "What is the Americans with Disabilities Act (ADA)?",
    answer: "Federal law requiring accessibility in public accommodations.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Applies to commercial properties open to public; requires reasonable accommodations and modifications."
  },
  {
    id: 345,
    question: "What is the Fair Housing Poster Requirement?",
    answer: "Must display fair housing poster in broker's office.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "easy",
    explanation: "Required by DRE; must be visible to public and include contact information for complaints."
  },
  {
    id: 346,
    question: "What is Advertising Compliance?",
    answer: "All advertising must comply with fair housing laws.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Cannot use discriminatory language or images; must include fair housing logo and broker information."
  },
  {
    id: 347,
    question: "What is the Equal Housing Opportunity Logo?",
    answer: "Required logo on all real estate advertising.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "easy",
    explanation: "Must appear on all advertisements; shows commitment to fair housing principles."
  },
  {
    id: 348,
    question: "What is Discriminatory Advertising?",
    answer: "Advertising that excludes or discourages protected classes.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Examples: 'adults only,' 'no children,' or 'Christian neighborhood.' Violates fair housing laws."
  },
  {
    id: 349,
    question: "What is the Real Estate Settlement Procedures Act (RESPA)?",
    answer: "Federal law regulating real estate settlement services.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Prohibits kickbacks, requires disclosure of settlement costs, and regulates escrow accounts."
  },
  {
    id: 350,
    question: "What is RESPA Kickback Prohibition?",
    answer: "Prohibits unearned fees for referrals.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Agents cannot receive payments for referring business unless providing actual services."
  },
  {
    id: 351,
    question: "What is the Truth in Lending Act (TILA)?",
    answer: "Federal law requiring disclosure of loan terms.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Requires lenders to disclose APR, finance charges, and payment schedules before closing."
  },
  {
    id: 352,
    question: "What is the Home Mortgage Disclosure Act (HMDA)?",
    answer: "Federal law requiring reporting of mortgage data.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Requires lenders to report loan applications and approvals to detect discriminatory lending patterns."
  },
  {
    id: 353,
    question: "What is the Equal Credit Opportunity Act (ECOA)?",
    answer: "Federal law prohibiting credit discrimination.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Prohibits discrimination in credit decisions based on race, religion, sex, national origin, age, or marital status."
  },
  {
    id: 354,
    question: "What is the Community Reinvestment Act (CRA)?",
    answer: "Federal law encouraging bank investment in low-income communities.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Requires banks to meet credit needs of communities where they operate, including low-income areas."
  },
  {
    id: 355,
    question: "What is Predatory Lending?",
    answer: "Unfair or abusive lending practices targeting vulnerable borrowers.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Includes excessive fees, balloon payments, and loans borrowers cannot afford. Illegal under federal and state law."
  },
  {
    id: 356,
    question: "What is the Homeowners Protection Act (HPA)?",
    answer: "Federal law regulating private mortgage insurance.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Requires automatic termination of PMI when loan-to-value ratio reaches 78%."
  },
  {
    id: 357,
    question: "What is the Real Estate Appraiser Act?",
    answer: "California law regulating real estate appraisers.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Requires licensing for appraisers; establishes standards and disciplinary procedures."
  },
  {
    id: 358,
    question: "What is USPAP?",
    answer: "Uniform Standards of Professional Appraisal Practice.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "National standards for appraisers; required for federally related transactions."
  },
  {
    id: 359,
    question: "What is the Subdivided Lands Law?",
    answer: "California law regulating sale of subdivided property.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Requires public report before selling subdivided land; administered by DRE."
  },
  {
    id: 360,
    question: "What is a Public Report?",
    answer: "Disclosure document required for subdivided land sales.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Issued by DRE; discloses material facts about subdivision including utilities, roads, and restrictions."
  },
  {
    id: 361,
    question: "What is the Subdivision Map Act?",
    answer: "California law regulating physical subdivision of land.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Requires local approval for land division; ensures proper planning and infrastructure."
  },
  {
    id: 362,
    question: "What is the California Environmental Quality Act (CEQA)?",
    answer: "State law requiring environmental review of significant projects.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Requires Environmental Impact Reports for projects affecting environment; affects development approvals."
  },
  {
    id: 363,
    question: "What is an Environmental Impact Report (EIR)?",
    answer: "Document analyzing environmental effects of proposed project.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Required under CEQA for significant projects; must identify and mitigate environmental impacts."
  },
  {
    id: 364,
    question: "What is the Coastal Act?",
    answer: "California law protecting coastal resources.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Regulates development in coastal zone; requires permits from Coastal Commission."
  },
  {
    id: 365,
    question: "What is the California Coastal Commission?",
    answer: "State agency regulating coastal development.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Issues permits for development in coastal zone; protects natural resources and public access."
  },
  {
    id: 366,
    question: "What is the Williamson Act?",
    answer: "California law providing tax incentives for agricultural land preservation.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Reduces property taxes for landowners who agree to keep land in agricultural use for 10+ years."
  },
  {
    id: 367,
    question: "What is the California Land Conservation Act?",
    answer: "Another name for the Williamson Act.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Provides property tax relief for agricultural land; encourages preservation of farmland."
  },
  {
    id: 368,
    question: "What is the California Land Use Planning Act?",
    answer: "State law requiring local governments to plan for growth.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Requires comprehensive general plans; ensures coordinated development and resource protection."
  },
  {
    id: 369,
    question: "What is a General Plan?",
    answer: "Comprehensive long-term plan for community development.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Required by state law; covers land use, housing, transportation, and environmental protection."
  },
  {
    id: 370,
    question: "What is a Specific Plan?",
    answer: "Detailed plan for specific area or project.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Implements general plan policies; provides detailed development standards and infrastructure requirements."
  },
  {
    id: 371,
    question: "What is a Zoning Ordinance?",
    answer: "Local law regulating land use and development.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Implements general plan; establishes zones, uses, and development standards."
  },
  {
    id: 372,
    question: "What is a Conditional Use Permit (CUP)?",
    answer: "Permission for land use not ordinarily allowed in zone.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Granted for uses beneficial to public; may include conditions to minimize impacts."
  },
  {
    id: 373,
    question: "What is a Variance?",
    answer: "Permission to deviate from zoning requirements.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Granted for unique circumstances; cannot change zoning classification."
  },
  {
    id: 374,
    question: "What is a Nonconforming Use?",
    answer: "Existing use that doesn't comply with current zoning.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Allowed to continue if established before zoning change; may lose status if discontinued."
  },
  {
    id: 375,
    question: "What is a Building Permit?",
    answer: "Official authorization to begin construction.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "easy",
    explanation: "Required for most construction; ensures compliance with building codes and zoning."
  },
  {
    id: 376,
    question: "What is a Certificate of Occupancy?",
    answer: "Document certifying structure is safe for use.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Issued after final inspection; required before occupancy or utility connection."
  },
  {
    id: 377,
    question: "What is a Certificate of Compliance?",
    answer: "Document certifying property meets zoning requirements.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Issued by planning department; confirms property conforms to current zoning."
  },
  {
    id: 378,
    question: "What is a Lot Line Adjustment?",
    answer: "Minor boundary change between adjacent parcels.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Does not create new parcels; requires approval from planning department."
  },
  {
    id: 379,
    question: "What is a Lot Merger?",
    answer: "Combining adjacent parcels under single ownership.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Eliminates lot lines; requires approval and recording of merger document."
  },
  {
    id: 380,
    question: "What is a Tentative Map?",
    answer: "Preliminary approval for subdivision.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Shows proposed lot layout and improvements; must be approved before final map."
  },
  {
    id: 381,
    question: "What is a Final Map?",
    answer: "Final approval for subdivision.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Recorded document creating new parcels; requires completion of improvements."
  },
  {
    id: 382,
    question: "What is a Parcel Map?",
    answer: "Simplified subdivision for 4 or fewer lots.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Less complex than final map; used for small subdivisions."
  },
  {
    id: 383,
    question: "What is a Vesting Tentative Map?",
    answer: "Tentative map with vested rights.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Protects developer from zoning changes; valid for specified period."
  },
  {
    id: 384,
    question: "What are Vested Rights?",
    answer: "Protection from zoning changes after approval.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Developer can complete project under original zoning; prevents mid-project rule changes."
  },
  {
    id: 385,
    question: "What is a Development Agreement?",
    answer: "Contract between developer and local government.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Establishes development standards and timeline; provides vested rights protection."
  },
  {
    id: 386,
    question: "What is a Mitigation Fee?",
    answer: "Payment required to offset development impacts.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Funds infrastructure, schools, or environmental mitigation; must be reasonable and related to impacts."
  },
  {
    id: 387,
    question: "What is a School Impact Fee?",
    answer: "Fee for new school construction.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Required for residential development; funds new schools needed for additional students."
  },
  {
    id: 388,
    question: "What is a Traffic Impact Fee?",
    answer: "Fee for transportation improvements.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Required for development that increases traffic; funds road improvements and transit."
  },
  {
    id: 389,
    question: "What is a Park Impact Fee?",
    answer: "Fee for new park development.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Required for residential development; funds new parks and recreational facilities."
  },
  {
    id: 390,
    question: "What is a Water Impact Fee?",
    answer: "Fee for water system improvements.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Required for development that increases water demand; funds new water facilities."
  },
  {
    id: 391,
    question: "What is a Sewer Impact Fee?",
    answer: "Fee for wastewater system improvements.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Required for development that increases sewage; funds new treatment facilities."
  },
  {
    id: 392,
    question: "What is a Fire Impact Fee?",
    answer: "Fee for fire protection improvements.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Required for development that increases fire risk; funds new fire stations and equipment."
  },
  {
    id: 393,
    question: "What is a Police Impact Fee?",
    answer: "Fee for law enforcement improvements.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Required for development that increases crime risk; funds new police stations and personnel."
  },
  {
    id: 394,
    question: "What is a Library Impact Fee?",
    answer: "Fee for library improvements.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Required for development that increases library demand; funds new libraries and materials."
  },
  {
    id: 395,
    question: "What is a Community Facilities District (CFD)?",
    answer: "Special district for infrastructure financing.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Creates special tax district; funds infrastructure through Mello-Roos bonds."
  },
  {
    id: 396,
    question: "What is a Mello-Roos District?",
    answer: "Special tax district for infrastructure.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Imposes special tax on properties; funds infrastructure through bond sales."
  },
  {
    id: 397,
    question: "What is a Special Assessment District?",
    answer: "Area where property owners pay for specific improvements.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Funds local improvements like sidewalks or streetlights; only benefiting properties pay."
  },
  {
    id: 398,
    question: "What is a Business Improvement District (BID)?",
    answer: "Special district for commercial area improvements.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Funds marketing, maintenance, and improvements in commercial areas; paid by property owners."
  },
  {
    id: 399,
    question: "What is a Homeowners Association (HOA)?",
    answer: "Organization managing common areas and enforcing CC&Rs.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Governs subdivision or condominium; collects dues and maintains common areas."
  },
  {
    id: 400,
    question: "What are CC&Rs?",
    answer: "Covenants, Conditions, and Restrictions governing property use.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Private restrictions on property use; enforceable by HOA or neighbors."
  },
  {
    id: 401,
    question: "What is a Master Association?",
    answer: "HOA governing multiple subdivisions.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Manages common areas for entire development; individual HOAs manage specific neighborhoods."
  },
  {
    id: 402,
    question: "What is a Condominium Association?",
    answer: "HOA for condominium development.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Manages common areas and building maintenance; governed by condominium documents."
  },
  {
    id: 403,
    question: "What is a Planned Unit Development (PUD)?",
    answer: "Development with flexible zoning and design standards.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Allows mixed uses and innovative design; governed by development agreement and CC&Rs."
  },
  {
    id: 404,
    question: "What is a Common Interest Development (CID)?",
    answer: "Development with shared common areas.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Includes condominiums, planned developments, and stock cooperatives; governed by Davis-Stirling Act."
  },
  {
    id: 405,
    question: "What is the Davis-Stirling Act?",
    answer: "California law governing common interest developments.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "hard",
    explanation: "Establishes rights and responsibilities of HOAs and homeowners; covers governance and enforcement."
  },
  {
    id: 406,
    question: "What is a Reserve Study?",
    answer: "Analysis of HOA's long-term maintenance needs.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Required every three years; determines reserve funding needs for major repairs and replacements."
  },
  {
    id: 407,
    question: "What is Reserve Funding?",
    answer: "HOA's savings for major repairs and replacements.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Required by Davis-Stirling Act; must be adequate to fund identified maintenance needs."
  },
  {
    id: 408,
    question: "What is a Special Assessment?",
    answer: "Additional HOA fee for specific projects.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged when reserves are insufficient; requires member approval for large amounts."
  },
  {
    id: 409,
    question: "What is a Regular Assessment?",
    answer: "Monthly or annual HOA fee for operations.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "easy",
    explanation: "Covers maintenance, management, and insurance; set by HOA board within budget limits."
  },
  {
    id: 410,
    question: "What is a Transfer Fee?",
    answer: "Fee charged when property is sold.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged by HOA or management company; covers administrative costs of ownership transfer."
  },
  {
    id: 411,
    question: "What is a Capital Improvement?",
    answer: "Major addition or enhancement to common areas.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Examples: new pool, clubhouse, or landscaping; requires member approval and adequate funding."
  },
  {
    id: 412,
    question: "What is a Maintenance Assessment?",
    answer: "Fee for ongoing maintenance and repairs.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "easy",
    explanation: "Covers routine maintenance like landscaping, cleaning, and minor repairs."
  },
  {
    id: 413,
    question: "What is a Reconstruction Assessment?",
    answer: "Fee for major repairs or reconstruction.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged when major components need replacement; may require member approval."
  },
  {
    id: 414,
    question: "What is a Working Capital Assessment?",
    answer: "Initial fee for new HOA operations.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to new homeowners; provides initial operating funds for HOA."
  },
  {
    id: 415,
    question: "What is a Reserve Assessment?",
    answer: "Fee to build up reserve funds.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged when reserves are below recommended levels; funds future maintenance needs."
  },
  {
    id: 416,
    question: "What is an Emergency Assessment?",
    answer: "Fee for urgent repairs or emergencies.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged for unexpected repairs; may not require member approval if urgent."
  },
  {
    id: 417,
    question: "What is a Budget Assessment?",
    answer: "Fee based on annual budget needs.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "easy",
    explanation: "Set annually by HOA board; covers projected operating expenses and reserve contributions."
  },
  {
    id: 418,
    question: "What is a Per-Unit Assessment?",
    answer: "Fee charged equally to all units.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "easy",
    explanation: "Each unit pays same amount regardless of size or value; most common assessment method."
  },
  {
    id: 419,
    question: "What is a Percentage Assessment?",
    answer: "Fee based on unit's percentage of total value.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Larger or more valuable units pay higher percentage; used in some condominiums."
  },
  {
    id: 420,
    question: "What is a Square Footage Assessment?",
    answer: "Fee based on unit's square footage.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Larger units pay more; used when units vary significantly in size."
  },
  {
    id: 421,
    question: "What is a Value-Based Assessment?",
    answer: "Fee based on unit's market value.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "More valuable units pay more; requires periodic value updates."
  },
  {
    id: 422,
    question: "What is a Time-Share Assessment?",
    answer: "Fee for time-share ownership.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged based on time-share usage; covers maintenance and management costs."
  },
  {
    id: 423,
    question: "What is a Fractional Assessment?",
    answer: "Fee for fractional ownership.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged based on ownership percentage; covers proportional share of costs."
  },
  {
    id: 424,
    question: "What is a Co-Op Assessment?",
    answer: "Fee for cooperative ownership.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to co-op shareholders; covers building maintenance and mortgage payments."
  },
  {
    id: 425,
    question: "What is a Mobile Home Assessment?",
    answer: "Fee for mobile home park.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to mobile home owners; covers park maintenance and utilities."
  },
  {
    id: 426,
    question: "What is a Commercial Assessment?",
    answer: "Fee for commercial property.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to commercial property owners; covers business district improvements."
  },
  {
    id: 427,
    question: "What is a Mixed-Use Assessment?",
    answer: "Fee for mixed-use development.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to mixed-use property owners; covers shared facilities and services."
  },
  {
    id: 428,
    question: "What is a Resort Assessment?",
    answer: "Fee for resort development.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to resort property owners; covers recreational facilities and services."
  },
  {
    id: 429,
    question: "What is a Golf Course Assessment?",
    answer: "Fee for golf course community.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to golf course community owners; covers course maintenance and operations."
  },
  {
    id: 430,
    question: "What is a Marina Assessment?",
    answer: "Fee for marina community.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to marina community owners; covers dock maintenance and water access."
  },
  {
    id: 431,
    question: "What is a Ski Resort Assessment?",
    answer: "Fee for ski resort community.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to ski resort community owners; covers ski facilities and mountain access."
  },
  {
    id: 432,
    question: "What is a Beach Community Assessment?",
    answer: "Fee for beach community.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to beach community owners; covers beach maintenance and access."
  },
  {
    id: 433,
    question: "What is a Mountain Community Assessment?",
    answer: "Fee for mountain community.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to mountain community owners; covers mountain access and facilities."
  },
  {
    id: 434,
    question: "What is a Desert Community Assessment?",
    answer: "Fee for desert community.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to desert community owners; covers desert facilities and services."
  },
  {
    id: 435,
    question: "What is a Wine Country Assessment?",
    answer: "Fee for wine country community.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to wine country community owners; covers wine facilities and services."
  },
  {
    id: 436,
    question: "What is a Coastal Community Assessment?",
    answer: "Fee for coastal community.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to coastal community owners; covers coastal facilities and access."
  },
  {
    id: 437,
    question: "What is a Lake Community Assessment?",
    answer: "Fee for lake community.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to lake community owners; covers lake maintenance and access."
  },
  {
    id: 438,
    question: "What is a River Community Assessment?",
    answer: "Fee for river community.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to river community owners; covers river access and facilities."
  },
  {
    id: 439,
    question: "What is a Forest Community Assessment?",
    answer: "Fee for forest community.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to forest community owners; covers forest maintenance and access."
  },
  {
    id: 440,
    question: "What is a Ranch Community Assessment?",
    answer: "Fee for ranch community.",
    category: "Practice of Real Estate and Mandated Disclosures",
    difficulty: "medium",
    explanation: "Charged to ranch community owners; covers ranch facilities and services."
  },

  // Contracts (60 cards)
  {
    id: 441,
    question: "What is a contract?",
    answer: "A legally enforceable agreement between two or more parties.",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Requires mutual consent, lawful object, capable parties, and consideration under California Civil Code 1550."
  },
  {
    id: 442,
    question: "What are the essential elements of a valid contract?",
    answer: "Competent parties, mutual consent, lawful object, and consideration.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "If any element is missing, the contract is void or voidable under California law."
  },
  {
    id: 443,
    question: "What is mutual consent (meeting of the minds)?",
    answer: "Agreement between parties on the same terms and conditions.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Formed through offer and acceptance; must be free of fraud, duress, or mistake."
  },
  {
    id: 444,
    question: "What is an offer?",
    answer: "A proposal by one party to enter into a contract on specific terms.",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Becomes binding when accepted exactly as stated and communicated to offeror."
  },
  {
    id: 445,
    question: "What is a counteroffer?",
    answer: "Response to an offer that changes one or more terms.",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Legally acts as a rejection of the original offer and creates a new one."
  },
  {
    id: 446,
    question: "What is consideration?",
    answer: "Something of value exchanged between parties.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Can be money, property, or a promise. Without consideration, there is no binding contract."
  },
  {
    id: 447,
    question: "What is lawful object?",
    answer: "The purpose of a contract must be legal and not against public policy.",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Illegal contracts are void and unenforceable in California."
  },
  {
    id: 448,
    question: "What is competent parties?",
    answer: "Parties must have legal capacity to enter into contracts.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Minors, mentally incompetent persons, and intoxicated persons lack capacity."
  },
  {
    id: 449,
    question: "What is acceptance?",
    answer: "Agreement to the terms of an offer.",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Must be unconditional and communicated to the offeror to create a binding contract."
  },
  {
    id: 450,
    question: "What is revocation?",
    answer: "Withdrawal of an offer before acceptance.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Offeror can revoke offer at any time before acceptance, unless consideration was given to keep it open."
  },
  {
    id: 451,
    question: "What is rejection?",
    answer: "Refusal to accept an offer.",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Terminates the offer; offeree cannot later accept the rejected offer."
  },
  {
    id: 452,
    question: "What is lapse?",
    answer: "Offer expires due to passage of time.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Offers expire after reasonable time unless specific deadline is set."
  },
  {
    id: 453,
    question: "What is death or incapacity?",
    answer: "Offer terminates if offeror dies or becomes incapacitated.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Personal offers cannot be accepted after offeror's death; estate offers may continue."
  },
  {
    id: 454,
    question: "What is an option contract?",
    answer: "Contract giving right to buy property at set price within time limit.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Requires consideration to keep offer open; buyer has exclusive right to purchase."
  },
  {
    id: 455,
    question: "What is earnest money?",
    answer: "Deposit showing buyer's serious intent to purchase.",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Held in escrow; forfeited if buyer defaults, applied to purchase price if sale closes."
  },
  {
    id: 456,
    question: "What is a purchase agreement?",
    answer: "Contract for sale of real property.",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Must be in writing under Statute of Frauds; includes all terms and conditions."
  },
  {
    id: 457,
    question: "What is the Statute of Frauds?",
    answer: "Law requiring certain contracts to be in writing.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Real estate contracts must be written and signed by parties to be enforceable."
  },
  {
    id: 458,
    question: "What is specific performance?",
    answer: "Court order requiring party to fulfill contract terms.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Available for real estate contracts since each property is unique; money damages insufficient."
  },
  {
    id: 459,
    question: "What is breach of contract?",
    answer: "Failure to perform contractual obligations.",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Gives non-breaching party right to damages or specific performance."
  },
  {
    id: 460,
    question: "What is anticipatory breach?",
    answer: "Party indicates they will not perform before performance is due.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Allows other party to sue immediately without waiting for performance date."
  },
  {
    id: 461,
    question: "What is rescission?",
    answer: "Cancellation of contract returning parties to original position.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Available for fraud, mistake, or mutual agreement; restores parties to pre-contract status."
  },
  {
    id: 462,
    question: "What is novation?",
    answer: "Substitution of new contract or party for existing contract.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Releases original party from obligations; requires consent of all parties."
  },
  {
    id: 463,
    question: "What is assignment?",
    answer: "Transfer of contract rights to another party.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Assignee receives benefits but not obligations; assignor remains liable for performance."
  },
  {
    id: 464,
    question: "What is delegation?",
    answer: "Transfer of contract duties to another party.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Delegator remains liable if delegatee fails to perform; requires consent of other party."
  },
  {
    id: 465,
    question: "What is liquidated damages?",
    answer: "Pre-agreed amount for contract breach.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Must be reasonable estimate of actual damages; excessive amounts are unenforceable."
  },
  {
    id: 466,
    question: "What is mitigation of damages?",
    answer: "Duty to minimize losses from contract breach.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Non-breaching party cannot recover damages that could have been avoided."
  },
  {
    id: 467,
    question: "What is impossibility of performance?",
    answer: "Contract cannot be performed due to unforeseen circumstances.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Excuses performance if event was unforeseeable and not caused by either party."
  },
  {
    id: 468,
    question: "What is frustration of purpose?",
    answer: "Contract purpose destroyed by unforeseen events.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Excuses performance when contract becomes worthless due to changed circumstances."
  },
  {
    id: 469,
    question: "What is duress?",
    answer: "Threat or coercion forcing someone to enter contract.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Makes contract voidable; victim can rescind if threat was improper and caused consent."
  },
  {
    id: 470,
    question: "What is undue influence?",
    answer: "Improper pressure from trusted person affecting contract decision.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Makes contract voidable; occurs when one party dominates the other's will."
  },
  {
    id: 471,
    question: "What is fraud?",
    answer: "Intentional misrepresentation inducing contract formation.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Makes contract voidable; requires false statement, knowledge of falsity, intent to deceive, and reliance."
  },
  {
    id: 472,
    question: "What is misrepresentation?",
    answer: "False statement of material fact.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Can be innocent, negligent, or fraudulent; innocent misrepresentation makes contract voidable."
  },
  {
    id: 473,
    question: "What is mistake?",
    answer: "Error in understanding contract terms or facts.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Mutual mistake makes contract void; unilateral mistake may make contract voidable."
  },
  {
    id: 474,
    question: "What is mutual mistake?",
    answer: "Both parties share same mistaken belief about material fact.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Makes contract void; both parties must be mistaken about same material fact."
  },
  {
    id: 475,
    question: "What is unilateral mistake?",
    answer: "Only one party is mistaken about contract terms or facts.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Generally does not excuse performance unless other party knew of mistake."
  },
  {
    id: 476,
    question: "What is unconscionability?",
    answer: "Contract terms so unfair they shock the conscience.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Makes contract unenforceable; requires both procedural and substantive unconscionability."
  },
  {
    id: 477,
    question: "What is capacity?",
    answer: "Legal ability to enter into binding contracts.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Minors, mentally incompetent persons, and intoxicated persons lack capacity."
  },
  {
    id: 478,
    question: "What is minority?",
    answer: "Legal status of persons under age of majority.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Minors can void contracts but must return consideration if still in their possession."
  },
  {
    id: 479,
    question: "What is ratification?",
    answer: "Approval of contract after reaching majority or regaining capacity.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Makes contract binding; can be express or implied through conduct."
  },
  {
    id: 480,
    question: "What is disaffirmance?",
    answer: "Rejection of contract by minor or incompetent person.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Must return consideration if still in possession; voids the contract."
  },
  {
    id: 481,
    question: "What is necessaries?",
    answer: "Goods and services essential for minor's welfare.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Minors are liable for reasonable value of necessaries even if contract is voidable."
  },
  {
    id: 482,
    question: "What is emancipation?",
    answer: "Legal process freeing minor from parental control.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Emancipated minors have full contractual capacity; can enter binding agreements."
  },
  {
    id: 483,
    question: "What is mental incompetency?",
    answer: "Lack of mental capacity to understand contract terms.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Makes contracts voidable; person or guardian can rescind within reasonable time."
  },
  {
    id: 484,
    question: "What is intoxication?",
    answer: "State of impaired judgment affecting contractual capacity.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Makes contracts voidable if intoxication prevents understanding of contract terms."
  },
  {
    id: 485,
    question: "What is parol evidence rule?",
    answer: "Rule limiting evidence of prior oral agreements when written contract exists.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Prevents introduction of oral evidence that contradicts written contract terms."
  },
  {
    id: 486,
    question: "What is integration clause?",
    answer: "Contract provision stating it contains entire agreement.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Prevents introduction of parol evidence; makes contract final and complete."
  },
  {
    id: 487,
    question: "What is condition precedent?",
    answer: "Event that must occur before contract obligation becomes due.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Example: buyer must obtain financing before purchase obligation becomes binding."
  },
  {
    id: 488,
    question: "What is condition subsequent?",
    answer: "Event that terminates contract obligation after it becomes due.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Example: lease terminates if tenant fails to pay rent within specified time."
  },
  {
    id: 489,
    question: "What is condition concurrent?",
    answer: "Obligations that must be performed simultaneously.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Example: buyer pays purchase price while seller delivers deed at closing."
  },
  {
    id: 490,
    question: "What is time is of the essence?",
    answer: "Contract clause making time deadlines critical.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Failure to meet deadline constitutes material breach; allows contract termination."
  },
  {
    id: 491,
    question: "What is force majeure?",
    answer: "Contract clause excusing performance due to unforeseeable events.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Covers acts of God, war, or other events beyond party's control."
  },
  {
    id: 492,
    question: "What is waiver?",
    answer: "Voluntary relinquishment of contract right.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Can be express or implied; does not require consideration but must be intentional."
  },
  {
    id: 493,
    question: "What is estoppel?",
    answer: "Legal doctrine preventing party from denying facts they previously admitted.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Prevents injustice when party's conduct misleads another to their detriment."
  },
  {
    id: 494,
    question: "What is accord and satisfaction?",
    answer: "Agreement to accept different performance than originally contracted.",
    category: "Contracts",
    difficulty: "hard",
    explanation: "Discharges original obligation; requires new agreement and actual performance."
  },
  {
    id: 495,
    question: "What is release?",
    answer: "Document relinquishing legal claims against another party.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Settles disputes; prevents future lawsuits on same matter."
  },
  {
    id: 496,
    question: "What is covenant?",
    answer: "Promise in deed or contract.",
    category: "Contracts",
    difficulty: "easy",
    explanation: "Creates legal obligation; breach allows damages or specific performance."
  },
  {
    id: 497,
    question: "What is warranty?",
    answer: "Promise guaranteeing certain facts or conditions.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Breach allows damages; can be express or implied."
  },
  {
    id: 498,
    question: "What is representation?",
    answer: "Statement of fact inducing contract formation.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "False representation can make contract voidable; differs from warranty in legal effect."
  },
  {
    id: 499,
    question: "What is warranty deed?",
    answer: "Deed with covenants guaranteeing title quality.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "Grantor warrants clear title and defends against all claims; strongest deed type."
  },
  {
    id: 500,
    question: "What is quitclaim deed?",
    answer: "Deed transferring only grantor's interest without warranties.",
    category: "Contracts",
    difficulty: "medium",
    explanation: "No title guarantees; used to clear title defects or transfer between family members."
  }
];